"use client";

import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

type User = {
  id: number;
  name: string;
  avatar: string;
  theme_color: string;
  calorie_goal: number;
  daily_budget_cents: number;
  weekly_budget_cents: number;
  monthly_budget_cents: number;
  height_cm: number;
  sex: "" | "女" | "男";
  birth_year: number;
  activity_level: "久坐" | "轻度" | "中度" | "高度";
  target_weight_grams: number;
  weekly_loss_grams: number;
  /** 税后收入。0 = 还没填真实时薪，界面上所有时间成本整块不出现。 */
  take_home_cents: number;
  pay_period: "月" | "年";
  /** 天数和小时都是「×10 的整数」（5.5 天 = 55），跟后端一致，不碰浮点。 */
  days_per_week_x10: number;
  hours_per_day_x10: number;
  commute_minutes: number;
  extra_minutes: number;
  time_as_workdays: number;
  /** 上班的日子，"1,2,3,4,5"（1=周一 … 7=周日） */
  work_days: string;
  /** ⚠️ 旧的全局班表，只用来给 work_schedule 打底，之后不再读。 */
  work_start: string;
  work_end: string;
  /** 一周总工时（分钟），由班表算出来 —— 真实时薪的分母用它。 */
  week_work_minutes: number;
};

/** 班表：一周七天各一条，像闹钟那样分别设。 */
type ScheduleRow = {
  user_id: number;
  weekday: number;
  enabled: number;
  start_time: string;
  end_time: string;
};

type WeightEntry = {
  id: number;
  user_id: number;
  weight_grams: number;
  local_date: string;
  note: string;
};

type CalorieEntry = {
  id: number;
  user_id: number;
  food_name: string;
  calories: number;
  meal_type: string;
  local_date: string;
  occurred_at: string;
};

type Expense = {
  id: number;
  payer_user_id: number;
  amount_cents: number;
  category: string;
  scope: "个人" | "共同";
  note: string;
  sentiment: "" | "值" | "一般" | "后悔";
  local_date: string;
  spent_at: string;
};

type CommonFood = {
  id: number;
  user_id: number;
  name: string;
  calories: number;
  default_meal: string;
  emoji: string;
};

/** 请假。**只记例外** —— 没有记录就是那天按班表正常上班。 */
type LeaveDay = {
  id: number;
  user_id: number;
  local_date: string;
  kind: "全天" | "半天";
  note: string;
};

type Settlement = {
  id: number;
  user_id: number;
  local_date: string;
  budget_cents: number;
  spent_cents: number;
  saved_cents: number;
  settled_at?: string;
};

type AppData = {
  me: User;
  users: User[];
  calories: CalorieEntry[];
  expenses: Expense[];
  foods: CommonFood[];
  settlements: Settlement[];
  weights: WeightEntry[];
  leaves: LeaveDay[];
  schedule: ScheduleRow[];
};

type View = "首页" | "卡路里" | "体重" | "记账" | "设置";
type Sheet =
  | { type: "calorie"; item?: CalorieEntry; preset?: CommonFood }
  | { type: "expense"; item?: Expense }
  | { type: "food"; item?: CommonFood }
  | { type: "weight"; item?: WeightEntry }
  | null;

type Celebration = { message: string; dual: boolean; saved: number } | null;

const NAV: Array<{ name: View; icon: string }> = [
  { name: "首页", icon: "🏡" },
  { name: "卡路里", icon: "🍡" },
  { name: "体重", icon: "🎈" },
  { name: "记账", icon: "🪙" },
  { name: "设置", icon: "🎀" },
];

const MEALS = ["早餐", "午餐", "晚餐", "宵夜", "零食"];
const MEAL_ICON: Record<string, string> = {
  早餐: "🌅",
  午餐: "🌤️",
  晚餐: "🌙",
  宵夜: "🌃",
  零食: "🍪",
};
/** 花的当下觉得值不值 —— 跟时间成本配一起看才有意思：「这 2 小时花得值吗」。 */
const SENTIMENTS = ["值", "一般", "后悔"];
const SENTIMENT_ICON: Record<string, string> = { 值: "😊", 一般: "😐", 后悔: "😞" };
const CATEGORIES = ["吃饭", "交通", "购物", "娱乐", "约会", "其他"];
const CATEGORY_ICON: Record<string, string> = {
  吃饭: "🍜",
  交通: "🚌",
  购物: "🛍️",
  娱乐: "🎮",
  约会: "💘",
  其他: "🧺",
};

/** 结算最多往回补 60 天，和后端 SETTLEMENT_BACKFILL_DAYS 保持一致。 */
const BACKFILL_DAYS = 60;
/** 无操作 60 分钟自动登出。 */
const IDLE_LIMIT_MS = 60 * 60 * 1000;
/** 每 20 秒检查一次「最后活动时间」，而不是每次事件都重设定时器。 */
const IDLE_CHECK_MS = 20 * 1000;

function localDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(date: string, amount: number): string {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() + amount);
  return localDate(value);
}

function mondayOf(date: string): string {
  const value = new Date(`${date}T12:00:00`);
  const day = value.getDay() || 7;
  value.setDate(value.getDate() - day + 1);
  return localDate(value);
}

function monthStart(date: string): string {
  return `${date.slice(0, 8)}01`;
}

function rm(cents: number): string {
  return `RM${(cents / 100).toFixed(2)}`;
}

/* ───────────────────────────── 钱 → 生命时间 ─────────────────────────────
   一笔钱值多少「命」，算的是**真实时薪**：税后收入不是除以工时，而是除以
   为了赚它而卖掉的全部时间 —— 工时 + 通勤 + 其他无偿时间。所以通勤越长，
   你的时间越便宜，同一杯奶茶就越贵。

   🔴 两个人各填各的时薪，于是**同一笔共同支出对两个人是不同的时间**。
      一顿 AA 的晚餐，可能是她的 2 小时、他的 1.2 小时。这是双人版才有的账。

   下面这几个是纯函数，不碰 React、不碰网络，改起来放心。            */

const WEEKS_PER_MONTH = 52 / 12;

/** 这个人填过时薪没有。没填就整块不显示时间，而不是显示 0 分钟。 */
function hasRate(user: Pick<User, "take_home_cents">): boolean {
  return user.take_home_cents > 0;
}

/**
 * 一个月为了赚钱总共卖掉多少小时（含通勤和其他无偿时间）。
 * 🔴 工时用**一周总分钟**，不用「天数 × 每天几小时」——
 *    周六上半天的话，后者算出来是错的，而且错得不显眼。
 */
function lifeHoursPerMonth(user: User): number {
  const unpaidHours = (user.commute_minutes + user.extra_minutes) / 60;
  const weekHours = (user.week_work_minutes ?? 2400) / 60;
  const workDays = user.days_per_week_x10 / 10;
  return (weekHours + workDays * unpaidHours) * WEEKS_PER_MONTH;
}

/** 真实时薪，单位「分／小时」。没填或数据不合理时回 0。 */
function centsPerHour(user: User): number {
  if (!hasRate(user)) return 0;
  const monthly = user.pay_period === "月" ? user.take_home_cents : user.take_home_cents / 12;
  const hours = lifeHoursPerMonth(user);
  return hours > 0 ? monthly / hours : 0;
}

function hoursOfCents(cents: number, user: User): number {
  const perHour = centsPerHour(user);
  return perHour > 0 ? cents / perHour : 0;
}

/**
 * 小时数变成人看的字。
 * 不到 1 小时给分钟（「42 分钟」比「0.7 小时」好懂），
 * 10 小时以内留一位小数，再往上就取整 —— 精确到 0.1 小时没有意义。
 */
function lifeText(hours: number, user: User): string {
  if (!Number.isFinite(hours) || hours <= 0) return "0 分钟";
  const perDay = user.hours_per_day_x10 / 10;
  if (user.time_as_workdays && perDay > 0 && hours >= perDay) {
    const days = Math.floor(hours / perDay);
    const rest = hours - days * perDay;
    return rest < 0.05 ? `${days} 个工作日` : `${days} 天 ${Math.round(rest)} 小时`;
  }
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} 分钟`;
  if (hours < 10) return `${hours.toFixed(1)} 小时`;
  return `${Math.round(hours)} 小时`;
}

/** 一笔钱 = 多少命。没填时薪回 null，调用方据此整块不渲染。 */
function lifeOf(cents: number, user: User): string | null {
  if (!hasRate(user) || cents <= 0) return null;
  return lifeText(hoursOfCents(cents, user), user);
}

/* ── 班表 · 请假 · 实时工钱 ──────────────────────────────────────────────
   🔴 **不用打卡。** 默认你按班表在上班，只有请假才记一条 ——
      打卡的坏处是「忘了按 = 一整天白干」，而那种错没有任何东西会提醒你。

   钱怎么算：**每天该拿的都一样**（月薪 ÷ 周期天数），因为月薪买断的是
   整个月，周末和假期照发。区别只在**什么时候到账**：
     · 工作日 —— 在上班那几个钟头里一点点到账，**下班那一刻拿满，然后停住**；
     · 休息日 / 带薪假 —— 没有班要上，那天的钱直接算满。

   🔴 上一版按「一天 24 小时」摊是错的：人都下班 9 小时了，数字还在慢慢爬，
      看起来像在替你加班。钱该停的时候必须停得住。                 */

/**
 * 班表的两种形状：
 *   存的是**七行**（一天一行），因为所有计算都要问「星期三那天几点到几点」；
 *   填的是**时段块**（一个时间 + 勾选适用哪几天），因为人是这么想的 ——
 *   「九点到六点，周一到五」，而不是把同一个时间抄五遍。
 * 下面两个函数在两种形状之间来回转。
 */
type ShiftBlock = { start: string; end: string; days: number[] };

/** 七行 → 时段块：把时间一样的几天并成一块。 */
function rowsToBlocks(rows: Array<{ weekday: number; enabled: boolean; start_time: string; end_time: string }>): ShiftBlock[] {
  const groups = new Map<string, ShiftBlock>();
  for (const row of rows) {
    if (!row.enabled) continue;
    const key = `${row.start_time}-${row.end_time}`;
    const block = groups.get(key) ?? { start: row.start_time, end: row.end_time, days: [] };
    block.days.push(row.weekday);
    groups.set(key, block);
  }
  const blocks = [...groups.values()];
  blocks.forEach((block) => block.days.sort((a, b) => a - b));
  blocks.sort((a, b) => (a.days[0] ?? 8) - (b.days[0] ?? 8));
  return blocks.length ? blocks : [{ start: "09:00", end: "18:00", days: [1, 2, 3, 4, 5] }];
}

/** 时段块 → 七行。没被任何一块勾到的日子就是休息。 */
function blocksToRows(blocks: ShiftBlock[]): Array<{ weekday: number; enabled: boolean; start_time: string; end_time: string }> {
  return [1, 2, 3, 4, 5, 6, 7].map((weekday) => {
    const block = blocks.find((item) => item.days.includes(weekday));
    return block
      ? { weekday, enabled: true, start_time: block.start, end_time: block.end }
      : { weekday, enabled: false, start_time: "09:00", end_time: "18:00" };
  });
}

/** 某人某个星期几的那一行班表。关掉或没有就当休息，回 null。 */
function scheduleFor(schedule: ScheduleRow[], userId: number, weekday: number): ScheduleRow | null {
  const row = schedule.find((item) => item.user_id === userId && item.weekday === weekday);
  return row && row.enabled ? row : null;
}

/** 一段班多少分钟。下班早于上班就当跨夜班绕一圈。 */
function shiftMinutes(start: string, end: string): number {
  const toMin = (value: string) => {
    const [hh, mm] = (value || "0:0").split(":").map(Number);
    return (Number.isFinite(hh) ? hh : 0) * 60 + (Number.isFinite(mm) ? mm : 0);
  };
  return (toMin(end) - toMin(start) + 1440) % 1440;
}

/** 「8 小时」「4.5 小时」这种给人看的字。 */
function shiftHoursText(start: string, end: string): string {
  const minutes = shiftMinutes(start, end);
  if (minutes < 30) return "时间填反了？";
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours} 小时` : `${hours.toFixed(1)} 小时`;
}

/** 某个日期是周几（1=周一 … 7=周日）。 */
function isoWeekday(date: string): number {
  const day = new Date(`${date}T12:00:00`).getDay();
  return day === 0 ? 7 : day;
}

function leaveOn(leaves: LeaveDay[], userId: number, date: string): LeaveDay | null {
  return leaves.find((item) => item.user_id === userId && item.local_date === date) ?? null;
}

/** 这天**按班表**该上几小时。休息日 0；全天假 0；半天假折半。 */
function scheduledHours(user: User, schedule: ScheduleRow[], leaves: LeaveDay[], date: string): number {
  const row = scheduleFor(schedule, user.id, isoWeekday(date));
  if (!row) return 0;
  const leave = leaveOn(leaves, user.id, date);
  if (leave?.kind === "全天") return 0;
  const full = shiftMinutes(row.start_time, row.end_time) / 60;
  return leave?.kind === "半天" ? full / 2 : full;
}

/** 这天上班的起点（本机时间），按当天那一行班表。 */
function workStartMs(user: User, schedule: ScheduleRow[], date: string): number {
  const row = scheduleFor(schedule, user.id, isoWeekday(date));
  const [hh, mm] = (row?.start_time || "09:00").split(":").map(Number);
  const at = new Date(`${date}T00:00:00`);
  at.setHours(Number.isFinite(hh) ? hh : 9, Number.isFinite(mm) ? mm : 0, 0, 0);
  return at.getTime();
}

/**
 * 这天到此刻为止工作了多少**秒**。
 * 今天算到当下并封顶在当天该上的小时数；过去的日子直接给满。
 */
function workedSecondsOn(user: User, schedule: ScheduleRow[], leaves: LeaveDay[], date: string, nowMs: number): number {
  const hours = scheduledHours(user, schedule, leaves, date);
  if (hours <= 0) return 0;
  const cap = hours * 3600;
  const elapsed = (nowMs - workStartMs(user, schedule, date)) / 1000;
  return Math.max(0, Math.min(cap, elapsed));
}

/** 一段班表里的「这个周期该上多少小时」（含今天已经上掉的部分）。 */
function workedSecondsInRange(user: User, schedule: ScheduleRow[], leaves: LeaveDay[], from: string, to: string, nowMs: number): number {
  let total = 0;
  for (let date = from; date <= to; date = addDays(date, 1)) {
    total += workedSecondsOn(user, schedule, leaves, date, nowMs);
  }
  return total;
}

/** 月薪换算成「一天发多少分」。周期长度按设置里的月起始日算。 */
function dailyPayCents(user: User, cycleDays: number): number {
  if (!hasRate(user) || cycleDays <= 0) return 0;
  const monthly = user.pay_period === "月" ? user.take_home_cents : user.take_home_cents / 12;
  return monthly / cycleDays;
}

/**
 * 这天的进度：0 = 还没开始，1 = 这天的钱已经拿满。
 * 🔴 **没有班要上的日子（休息日、带薪假）直接算 1** ——
 *    薪水买断整个月，不上班那天的钱也是你的，不该显示成 0。
 */
function dayProgress(user: User, schedule: ScheduleRow[], leaves: LeaveDay[], date: string, nowMs: number): number {
  const hours = scheduledHours(user, schedule, leaves, date);
  if (hours <= 0) return 1;
  const worked = workedSecondsOn(user, schedule, leaves, date, nowMs);
  return Math.max(0, Math.min(1, worked / (hours * 3600)));
}

/** 今天到此刻为止赚了多少分。下班拿满之后就不动了。 */
function earnedTodayCents(user: User, schedule: ScheduleRow[], leaves: LeaveDay[], cycleDays: number, nowMs: number): number {
  const perDay = dailyPayCents(user, cycleDays);
  if (perDay <= 0) return 0;
  return perDay * dayProgress(user, schedule, leaves, today0(nowMs), nowMs);
}

/** 本机时间的今天。 */
function today0(nowMs: number): string {
  return localDate(new Date(nowMs));
}

/**
 * 这个周期从头到此刻赚了多少分。
 * 过去的每一天都已经拿满（不管上没上班），今天按进度算。
 */
function earnedCycleCents(user: User, schedule: ScheduleRow[], leaves: LeaveDay[], cycleStart: string, cycleDays: number, nowMs: number): number {
  const perDay = dailyPayCents(user, cycleDays);
  if (perDay <= 0) return 0;
  const today = today0(nowMs);
  let days = 0;
  for (let date = cycleStart; date < today; date = addDays(date, 1)) days += 1;
  return perDay * Math.min(cycleDays, days + dayProgress(user, schedule, leaves, today, nowMs));
}

/** 「1 小时 31 分 30 秒」。秒是要一直在跳的，所以永远带上。 */
function clockText(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h} 小时 ${m} 分 ${sec} 秒`;
  if (m > 0) return `${m} 分 ${sec} 秒`;
  return `${sec} 秒`;
}

/** 钱要跳到「分」以下才看得出在动，所以显示到小数点后 2 位并保留尾数。 */
function rmLive(cents: number): string {
  return `RM${(Math.max(0, cents) / 100).toFixed(2)}`;
}

/**
 * 公式自检。/**
 * 公式自检。设置页会显示结果 —— 时薪是整个时间视图的地基，
 * 地基错了上面每一个数字都错，而那种错不会报错，只会安静地骗人。
 */
function rateSelfTest(): Array<[string, boolean]> {
  const base: User = {
    id: 0, name: "", avatar: "", theme_color: "#000000", calorie_goal: 2000,
    daily_budget_cents: 0, weekly_budget_cents: 0, monthly_budget_cents: 0,
    height_cm: 0, sex: "", birth_year: 0, activity_level: "轻度",
    target_weight_grams: 0, weekly_loss_grams: 0,
    take_home_cents: 320000, pay_period: "月", days_per_week_x10: 50,
    hours_per_day_x10: 80, commute_minutes: 0, extra_minutes: 0, time_as_workdays: 0,
    work_days: "1,2,3,4,5", work_start: "09:00", work_end: "17:00", week_work_minutes: 2400,
  };
  const withCommute: User = { ...base, commute_minutes: 90 };
  return [
    ["通勤会拉低真实时薪", centsPerHour(withCommute) < centsPerHour(base)],
    ["一杯奶茶不到一小时", hoursOfCents(600, withCommute) > 0 && hoursOfCents(600, withCommute) < 1],
    ["一个月房租超过 40 小时", hoursOfCents(85000, withCommute) > 40],
  ];
}

function clampPercent(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((value / total) * 100)));
}

function dateLabel(date: string): string {
  const value = new Date(`${date}T12:00:00`);
  return `${value.getMonth() + 1}/${value.getDate()}`;
}

function dayLabel(date: string): string {
  return ["日", "一", "二", "三", "四", "五", "六"][new Date(`${date}T12:00:00`).getDay()];
}

function friendlyDate(date: string, today: string): string {
  if (date === today) return "今天";
  if (date === addDays(today, -1)) return "昨天";
  if (date === addDays(today, -2)) return "前天";
  return `${date.slice(5, 7)}/${date.slice(8, 10)} 周${dayLabel(date)}`;
}

/**
 * 共同支出的分账规则（和后端 SHARE_SQL 完全一致）：
 * 付款那一方拿「向上取整」的一半，另一方拿「向下取整」的一半，
 * 两人相加永远等于原始金额，不会出现 RM0.01 摊成两个 RM0.01 的情况。
 */
function shareOf(item: Expense, userId: number): number {
  if (item.scope === "共同") {
    const half = Math.floor(item.amount_cents / 2);
    return item.payer_user_id === userId ? item.amount_cents - half : half;
  }
  return item.payer_user_id === userId ? item.amount_cents : 0;
}

function periodExpenses(expenses: Expense[], userId: number, from: string, to: string): number {
  return expenses
    .filter((item) => item.local_date >= from && item.local_date <= to)
    .reduce((sum, item) => sum + shareOf(item, userId), 0);
}

function calorieTotal(entries: CalorieEntry[], userId: number, from: string, to = from): number {
  return entries
    .filter((item) => item.user_id === userId && item.local_date >= from && item.local_date <= to)
    .reduce((sum, item) => sum + item.calories, 0);
}

/**
 * 连续达标天数：今天已经结算且超支 → 直接断掉；今天还没结算 → 从昨天往前数。
 * 因为只看 daily_settlements，撤销或补结算之后重新算出来的结果自动就是对的。
 */
function streakFor(settlements: Settlement[], userId: number, today: string): number {
  const mine = settlements.filter((item) => item.user_id === userId);
  const goodDates = new Set(mine.filter((item) => item.saved_cents >= 0).map((item) => item.local_date));
  const settledDates = new Set(mine.map((item) => item.local_date));
  let cursor = today;
  if (!goodDates.has(cursor)) {
    if (settledDates.has(cursor)) return 0;
    cursor = addDays(cursor, -1);
  }
  let count = 0;
  while (goodDates.has(cursor)) {
    count += 1;
    cursor = addDays(cursor, -1);
  }
  return count;
}

function coupleStreak(settlements: Settlement[], users: User[], today: string): number {
  const need = Math.max(1, users.length);
  const good = new Map<string, Set<number>>();
  const settled = new Map<string, Set<number>>();
  for (const item of settlements) {
    const settledIds = settled.get(item.local_date) ?? new Set<number>();
    settledIds.add(item.user_id);
    settled.set(item.local_date, settledIds);
    if (item.saved_cents < 0) continue;
    const ids = good.get(item.local_date) ?? new Set<number>();
    ids.add(item.user_id);
    good.set(item.local_date, ids);
  }
  let cursor = today;
  if ((good.get(cursor)?.size ?? 0) < need) {
    if ((settled.get(cursor)?.size ?? 0) >= need) return 0;
    cursor = addDays(cursor, -1);
  }
  let count = 0;
  while ((good.get(cursor)?.size ?? 0) >= need) {
    count += 1;
    cursor = addDays(cursor, -1);
  }
  return count;
}

/* --------------------------- 体重 / BMI / 每日建议 --------------------------- */

/** 主题色预设，都是低饱和的马卡龙色。 */
const THEME_PRESETS: Array<{ name: string; value: string }> = [
  { name: "奶蓝", value: "#8fbeea" },
  { name: "奶粉", value: "#f3a8c4" },
  { name: "薄荷", value: "#8fd3b6" },
  { name: "奶黄", value: "#f5ce7a" },
  { name: "丁香", value: "#bca4e8" },
  { name: "蜜桃", value: "#f5a98f" },
  { name: "天青", value: "#93cfdf" },
  { name: "抹茶", value: "#b6ce8e" },
];

const SEX_OPTIONS = ["女", "男"] as const;
const ACTIVITY_OPTIONS = ["久坐", "轻度", "中度", "高度"] as const;
const ACTIVITY_FACTOR: Record<string, number> = { 久坐: 1.2, 轻度: 1.375, 中度: 1.55, 高度: 1.725 };
const ACTIVITY_HINT: Record<string, string> = {
  久坐: "几乎不运动",
  轻度: "每周活动 1–3 次",
  中度: "每周活动 3–5 次",
  高度: "每周活动 6 次以上",
};
/** 每周减重档位（克）。上限 0.75kg —— 再快就不在健康范围里了。 */
const WEEKLY_LOSS_OPTIONS: Array<{ label: string; grams: number }> = [
  { label: "先维持", grams: 0 },
  { label: "0.25 公斤", grams: 250 },
  { label: "0.5 公斤", grams: 500 },
  { label: "0.75 公斤", grams: 750 },
];
/** 1 公斤脂肪约等于 7700 千卡，这是估算减重速度的通用换算。 */
const KCAL_PER_KG_FAT = 7700;
/** 每日摄入托底线，低于这个数就不健康了，无论怎么算都不会往下建议。 */
const MIN_DAILY_KCAL: Record<string, number> = { 女: 1200, 男: 1500, "": 1200 };
/** 亚洲标准（马来西亚卫生部）的 BMI 分界。 */
const BMI_NORMAL_LOW = 18.5;
const BMI_NORMAL_HIGH = 22.9;

function kgOf(grams: number): number {
  return grams / 1000;
}

function kgText(grams: number, digits = 1): string {
  return `${(grams / 1000).toFixed(digits)} 公斤`;
}

function bmiOf(weightGrams: number, heightCm: number): number {
  if (!weightGrams || !heightCm) return 0;
  const meters = heightCm / 100;
  return kgOf(weightGrams) / (meters * meters);
}

type BmiBand = { label: string; tone: "low" | "good" | "warn" | "high"; copy: string };

/** 亚洲标准：<18.5 偏瘦，18.5–22.9 正常，23–27.4 超重，≥27.5 肥胖。 */
function bmiBand(bmi: number): BmiBand {
  if (bmi < BMI_NORMAL_LOW) return { label: "偏瘦", tone: "low", copy: "可以再多吃一点，身体会开心的" };
  if (bmi <= BMI_NORMAL_HIGH) return { label: "正常", tone: "good", copy: "刚刚好，保持住就很棒了" };
  if (bmi < 27.5) return { label: "超重", tone: "warn", copy: "慢慢来，一点一点就好" };
  return { label: "肥胖", tone: "high", copy: "小步慢走，别跟自己较劲" };
}

/** 该身高对应的健康体重区间（克）。 */
function healthyWeightRange(heightCm: number): [number, number] {
  const meters = heightCm / 100;
  return [Math.round(BMI_NORMAL_LOW * meters * meters * 1000), Math.round(BMI_NORMAL_HIGH * meters * meters * 1000)];
}

type DailyPlan = {
  age: number;
  bmr: number;
  tdee: number;
  deficit: number;
  target: number;
  floor: number;
  capped: boolean;
  underweight: boolean;
};

/**
 * 用 Mifflin-St Jeor 公式估基础代谢，再乘活动系数得到每日消耗，
 * 最后按每周想减多少反推每日该吃多少。
 * 三条保护：已经偏瘦就不建议制造热量缺口；结果永远不低于托底线；每周减重最多 0.75 公斤。
 */
function dailyPlan(user: User, weightGrams: number, today: string): DailyPlan | null {
  if (!weightGrams || !user.height_cm || !user.sex || !user.birth_year) return null;
  const age = Number(today.slice(0, 4)) - user.birth_year;
  if (age < 10 || age > 120) return null;
  const base = 10 * kgOf(weightGrams) + 6.25 * user.height_cm - 5 * age;
  const bmr = Math.round(user.sex === "男" ? base + 5 : base - 161);
  const tdee = Math.round(bmr * (ACTIVITY_FACTOR[user.activity_level] ?? 1.375));
  const underweight = bmiOf(weightGrams, user.height_cm) < BMI_NORMAL_LOW;
  const wanted = Math.round((user.weekly_loss_grams / 1000) * KCAL_PER_KG_FAT / 7);
  const deficit = underweight ? 0 : wanted;
  const floor = MIN_DAILY_KCAL[user.sex] ?? 1200;
  let target = tdee - deficit;
  let capped = false;
  if (target < floor) {
    target = floor;
    capped = true;
  }
  return { age, bmr, tdee, deficit, target, floor, capped, underweight };
}

/** 某人按日期倒序的体重记录。 */
function weightsOf(weights: WeightEntry[], userId: number): WeightEntry[] {
  return weights
    .filter((item) => item.user_id === userId)
    .slice()
    .sort((a, b) => (a.local_date < b.local_date ? 1 : -1));
}

class RequestError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** 任意请求收到 401 时统一通知 App 回到登录页。 */
let onUnauthorized: ((message: string) => void) | null = null;

async function api<T>(url: string, options?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers: options?.body ? { "content-type": "application/json", ...options.headers } : options?.headers,
    });
  } catch {
    throw new RequestError("网络好像走神了，等一下再试试 🌧️", 0);
  }
  const payload = (await response.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!response.ok || payload === null) {
    const message = payload?.error ?? "请求失败，请再试一次";
    if (response.status === 401 && !url.startsWith("/api/auth/")) onUnauthorized?.(message);
    throw new RequestError(message, response.status);
  }
  return payload;
}

/**
 * 闲置自动登出。
 * 只在事件里记录「最后活动时间」，定时器固定 20 秒跑一次做比较，
 * 不会因为 scroll / touchstart 这种高频事件反复 clearTimeout + setTimeout。
 */
function useIdleTimeout(active: boolean, onTimeout: () => void) {
  const lastActiveRef = useRef(0);
  const firedRef = useRef(false);
  const callbackRef = useRef(onTimeout);

  useEffect(() => {
    callbackRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (!active) return;
    lastActiveRef.current = Date.now();
    firedRef.current = false;

    const touch = () => {
      lastActiveRef.current = Date.now();
    };
    const check = () => {
      if (firedRef.current) return;
      if (Date.now() - lastActiveRef.current >= IDLE_LIMIT_MS) {
        firedRef.current = true;
        callbackRef.current();
      }
    };
    // 从后台切回来（含 iOS Safari 的 BFCache 恢复）立刻检查一次是否已经超时。
    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      check();
      if (!firedRef.current) lastActiveRef.current = Date.now();
    };

    const events: Array<keyof WindowEventMap> = ["touchstart", "pointerdown", "click", "keydown", "scroll"];
    events.forEach((name) => window.addEventListener(name, touch, { passive: true }));
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("pageshow", onWake);
    window.addEventListener("focus", onWake);
    const timer = window.setInterval(check, IDLE_CHECK_MS);

    return () => {
      events.forEach((name) => window.removeEventListener(name, touch));
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("pageshow", onWake);
      window.removeEventListener("focus", onWake);
      window.clearInterval(timer);
    };
  }, [active]);
}

/* ---------------------------------- 原创小动物 ---------------------------------- */

type MascotKind = "bear" | "bunny";
type MascotMood = "happy" | "wink" | "sleepy" | "cheer";

/**
 * 全部用内联 SVG 画的原创情侣小动物（小熊 / 小兔），
 * 没有引用任何第三方形象或素材，颜色跟着各自的主题色走。
 */
function Mascot({ kind, color, mood = "happy", size = 76, className = "" }: {
  kind: MascotKind; color?: string; mood?: MascotMood; size?: number; className?: string;
}) {
  const base = color ?? (kind === "bear" ? "#A9CDEF" : "#F5B3CC");
  const ink = "#5C4A44";
  const blush = kind === "bear" ? "#F6BFC6" : "#F49BB8";
  const eyes = mood === "sleepy"
    ? (
      <>
        <path d="M35 51 q5 5 10 0" fill="none" stroke={ink} strokeWidth="3.4" strokeLinecap="round" />
        <path d="M55 51 q5 5 10 0" fill="none" stroke={ink} strokeWidth="3.4" strokeLinecap="round" />
      </>
    )
    : mood === "wink"
      ? (
        <>
          <circle cx="40" cy="52" r="4.2" fill={ink} />
          <path d="M55 52 q5 -5 10 0" fill="none" stroke={ink} strokeWidth="3.4" strokeLinecap="round" />
        </>
      )
      : mood === "cheer"
        ? (
          <>
            <path d="M35 54 q5 -8 10 0" fill="none" stroke={ink} strokeWidth="3.6" strokeLinecap="round" />
            <path d="M55 54 q5 -8 10 0" fill="none" stroke={ink} strokeWidth="3.6" strokeLinecap="round" />
          </>
        )
        : (
          <>
            <circle cx="40" cy="52" r="4.2" fill={ink} />
            <circle cx="60" cy="52" r="4.2" fill={ink} />
            <circle cx="41.4" cy="50.6" r="1.4" fill="#fff" />
            <circle cx="61.4" cy="50.6" r="1.4" fill="#fff" />
          </>
        );

  return (
    <svg className={`mascot ${className}`} width={size} height={size} viewBox="0 0 100 100" role="img" aria-hidden="true">
      {kind === "bear" ? (
        <>
          <circle cx="25" cy="27" r="14" fill={base} />
          <circle cx="25" cy="27" r="7" fill="#FFF1E6" />
          <circle cx="75" cy="27" r="14" fill={base} />
          <circle cx="75" cy="27" r="7" fill="#FFF1E6" />
        </>
      ) : (
        <>
          <ellipse cx="34" cy="20" rx="9" ry="21" fill={base} transform="rotate(-11 34 20)" />
          <ellipse cx="34" cy="21" rx="4.2" ry="14" fill="#FFF1E6" transform="rotate(-11 34 21)" />
          <ellipse cx="66" cy="20" rx="9" ry="21" fill={base} transform="rotate(11 66 20)" />
          <ellipse cx="66" cy="21" rx="4.2" ry="14" fill="#FFF1E6" transform="rotate(11 66 21)" />
        </>
      )}
      <circle cx="50" cy="55" r="33" fill={base} />
      <circle cx="50" cy="57" r="29" fill="#FFFBF5" opacity=".22" />
      <ellipse cx="50" cy="65" rx="17" ry="13" fill="#FFF6EC" />
      {eyes}
      <circle cx="28" cy="62" r="6.4" fill={blush} opacity=".62" />
      <circle cx="72" cy="62" r="6.4" fill={blush} opacity=".62" />
      {kind === "bear" ? (
        <ellipse cx="50" cy="60" rx="5" ry="3.6" fill={ink} />
      ) : (
        <path d="M46 59 h8 l-4 4.4 z" fill="#E68AA6" />
      )}
      <path d="M50 63.5 v3.4" fill="none" stroke={ink} strokeWidth="2.4" strokeLinecap="round" />
      <path d="M50 67 q-5.5 4.6 -9.5 .4" fill="none" stroke={ink} strokeWidth="2.6" strokeLinecap="round" />
      <path d="M50 67 q5.5 4.6 9.5 .4" fill="none" stroke={ink} strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

function mascotKindFor(user: User, users: User[]): MascotKind {
  const sorted = [...users].sort((a, b) => a.id - b.id);
  return sorted[0]?.id === user.id ? "bear" : "bunny";
}

/** 结算庆祝用的彩带：颜色、大小、飘落节奏都在这里算好，避免 CSS calc() 里没有的取模运算。 */
const CONFETTI_COLORS = ["#F9C8DA", "#C3DFF7", "#FBE3A6", "#C9E8D8", "#DCCBF6", "#FFD3C4"];
const CONFETTI: React.CSSProperties[] = Array.from({ length: 56 }, (_, index) => ({
  left: `${(index * 37) % 100}%`,
  width: `${8 + (index % 4) * 3}px`,
  height: `${10 + (index % 3) * 6}px`,
  borderRadius: index % 3 === 0 ? "50%" : index % 3 === 1 ? "5px" : "2px",
  background: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
  animationDuration: `${(2.4 + (index % 6) * 0.24).toFixed(2)}s`,
  animationDelay: `${(-(index % 11) * 0.22).toFixed(2)}s`,
  ["--dx" as string]: `${(index % 2 === 0 ? -34 : 46) + (index % 5) * 4}px`,
  ["--rot" as string]: `${180 + (index % 7) * 90}deg`,
}));

/** 卡片角落的小爪印装饰。 */
function PawDeco({ className = "" }: { className?: string }) {
  return (
    <svg className={`paw-deco ${className}`} viewBox="0 0 40 40" aria-hidden="true">
      <ellipse cx="20" cy="26" rx="10" ry="8.5" />
      <circle cx="9" cy="14" r="4.1" />
      <circle cx="17" cy="9.5" r="4.4" />
      <circle cx="26" cy="10.5" r="4.2" />
      <circle cx="33" cy="17" r="3.8" />
    </svg>
  );
}

/* ------------------------------------ 主应用 ----------------------------------- */

export default function Home() {
  const [today, setToday] = useState(() => localDate());
  const [profiles, setProfiles] = useState<User[]>([]);
  const [me, setMe] = useState<User | null>(null);
  const [data, setData] = useState<AppData | null>(null);
  const [view, setView] = useState<View>("首页");
  const [sheet, setSheet] = useState<Sheet>(null);
  const [settleOpen, setSettleOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [loginNotice, setLoginNotice] = useState("");
  const [celebration, setCelebration] = useState<Celebration>(null);
  const toastTimer = useRef<number | null>(null);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2800);
  }, []);

  const loadData = useCallback(async (day: string) => {
    setRefreshing(true);
    try {
      const start = [monthStart(day), addDays(day, -BACKFILL_DAYS)].sort()[0];
      const payload = await api<AppData & { ok: true }>(`/api/data?from=${start}&to=${day}`);
      // 万一后端还是旧版本（没有 weights 字段），这里兜一下，免得页面直接崩
      setData({ ...payload, weights: payload.weights ?? [] });
      setMe(payload.me);
    } catch (error) {
      if (!(error instanceof RequestError && error.status === 401)) {
        showToast(error instanceof Error ? error.message : "加载失败了，下拉再试试");
      }
    } finally {
      setRefreshing(false);
    }
  }, [showToast]);

  const goToLogin = useCallback(async (notice: string) => {
    setMe(null);
    setData(null);
    setSheet(null);
    setSettleOpen(false);
    setCelebration(null);
    setView("首页");
    setLoginNotice(notice);
    try {
      const payload = await api<{ users: User[] }>("/api/profiles");
      setProfiles(payload.users);
    } catch {
      // 拿不到最新头像也没关系，登录页会沿用上一次的列表
    }
  }, []);

  const endSession = useCallback(async (notice: string) => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {
      // 已经断网也要让界面回到登录页
    }
    await goToLogin(notice);
  }, [goToLogin]);

  // 任何 API 收到 401 都统一回登录页，不留在原界面报错
  useEffect(() => {
    if (!me) {
      onUnauthorized = null;
      return;
    }
    onUnauthorized = () => {
      void goToLogin("你走开太久啦，重新输入 PIN 吧 🐻💤");
    };
    return () => {
      onUnauthorized = null;
    };
  }, [me, goToLogin]);

  useIdleTimeout(Boolean(me), () => {
    void endSession("你走开太久啦，重新输入 PIN 吧 🐻💤");
  });

  // 跨过午夜时把「今天」换掉并重新拉数据，别让页面一直停在昨天
  const dayWatchRef = useRef<{ today: string; signedIn: boolean; load: (day: string) => Promise<void> }>({
    today,
    signedIn: false,
    load: loadData,
  });

  useEffect(() => {
    dayWatchRef.current = { today, signedIn: Boolean(me), load: loadData };
  }, [today, me, loadData]);

  useEffect(() => {
    const sync = () => {
      const watch = dayWatchRef.current;
      const now = localDate();
      if (now === watch.today) return;
      dayWatchRef.current = { ...watch, today: now };
      setToday(now);
      if (watch.signedIn) void watch.load(now);
    };
    const timer = window.setInterval(sync, 60_000);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("pageshow", sync);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("pageshow", sync);
    };
  }, []);

  const bootedRef = useRef(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const profilePayload = await api<{ users: User[] }>("/api/profiles");
        if (!active) return;
        setProfiles(profilePayload.users);
        try {
          const sessionPayload = await api<{ user: User }>("/api/session");
          if (!active) return;
          setMe(sessionPayload.user);
          await loadData(localDate());
        } catch {
          if (active) setMe(null);
        }
      } catch (error) {
        if (active) showToast(error instanceof Error ? error.message : "连不上小服务器，等下再试 🌧️");
      } finally {
        if (active) {
          bootedRef.current = true;
          setLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [loadData, showToast]);

  if (loading) return <LoadingScreen />;
  if (!me) {
    return (
      <LoginScreen
        profiles={profiles}
        notice={loginNotice}
        onLogin={async (userId, pin) => {
          const result = await api<{ user: User }>("/api/auth/login", {
            method: "POST",
            body: JSON.stringify({ user_id: userId, pin }),
          });
          setLoginNotice("");
          setMe(result.user);
          await loadData(localDate());
        }}
      />
    );
  }

  if (!data) return <LoadingScreen compact />;

  const current = data.users.find((user) => user.id === me.id) ?? me;
  const other = data.users.find((user) => user.id !== me.id) ?? data.users[0] ?? current;
  const myKind = mascotKindFor(current, data.users);
  const otherKind: MascotKind = myKind === "bear" ? "bunny" : "bear";

  const settleDay = async (date: string) => {
    try {
      const result = await api<{ saved_cents: number; under_budget: boolean; dual_celebration: boolean; message: string }>("/api/settlements", {
        method: "POST",
        body: JSON.stringify({ local_date: date }),
      });
      await loadData(today);
      if (navigator.vibrate) navigator.vibrate(24);
      if (result.under_budget) {
        setCelebration({
          message: result.dual_celebration ? "你们俩都守住了钱包！💑🎊" : result.message,
          dual: result.dual_celebration,
          saved: result.saved_cents,
        });
      } else {
        showToast(result.message);
      }
    } catch (error) {
      if (!(error instanceof RequestError && error.status === 401)) {
        showToast(error instanceof Error ? error.message : "结算没成功，再试一次");
      }
    }
  };

  const removeSettlement = async (date: string) => {
    if (!window.confirm(`要撤销 ${friendlyDate(date, today)} 的结算吗？撤销后随时可以重新结算哦。`)) return;
    try {
      await api("/api/settlements", { method: "DELETE", body: JSON.stringify({ local_date: date }) });
      showToast("已经撤销啦，重新结算就好 🧸");
      await loadData(today);
    } catch (error) {
      if (!(error instanceof RequestError && error.status === 401)) {
        showToast(error instanceof Error ? error.message : "撤销失败了");
      }
    }
  };

  const remove = async (kind: "calories" | "expenses" | "foods" | "weights", id: number) => {
    if (!window.confirm("确定要删掉这条记录吗？")) return;
    try {
      await api(`/api/${kind}/${id}`, { method: "DELETE" });
      showToast("已经帮你收起来啦 🧹");
      await loadData(today);
    } catch (error) {
      if (!(error instanceof RequestError && error.status === 401)) {
        showToast(error instanceof Error ? error.message : "删除失败了");
      }
    }
  };

  const mySettlements = data.settlements
    .filter((item) => item.user_id === current.id)
    .slice()
    .sort((a, b) => (a.local_date < b.local_date ? 1 : -1));

  return (
    <main className="app-shell" style={{ "--me-color": current.theme_color, "--other-color": other.theme_color } as React.CSSProperties}>
      <div className="sky-deco" aria-hidden="true">
        <i className="cloud cloud-a" />
        <i className="cloud cloud-b" />
        <i className="sparkle sparkle-a">✦</i>
        <i className="sparkle sparkle-b">✦</i>
      </div>

      <header className="topbar">
        <div>
          <p className="eyebrow">{today.slice(5, 7)}月{today.slice(8, 10)}日 · 两个人的小账本</p>
          <h1>{view === "首页" ? `嗨，${current.name}` : view}</h1>
        </div>
        <button className="avatar-button" aria-label="前往设置" onClick={() => setView("设置")}>
          <span>{current.avatar}</span>
          <i className="online-dot" />
        </button>
      </header>

      <section className="page-content" aria-live="polite">
        {view === "首页" && (
          <Dashboard
            data={data}
            current={current}
            other={other}
            myKind={myKind}
            otherKind={otherKind}
            today={today}
            refreshing={refreshing}
            onSettle={() => void settleDay(today)}
            onOpenSettle={() => setSettleOpen(true)}
            onAddCalorie={() => setSheet({ type: "calorie" })}
            onAddExpense={() => setSheet({ type: "expense" })}
            onLeave={async (date, kind) => {
              try {
                if (kind === null) {
                  await api(`/api/leaves/${date}`, { method: "DELETE" });
                  showToast("销假了，今天照常上班 ☀️");
                } else {
                  await api("/api/leaves", { method: "POST", body: JSON.stringify({ local_date: date, kind }) });
                  showToast("请假记下了，今天好好休息 🏖️");
                }
                if (navigator.vibrate) navigator.vibrate(18);
                await loadData(today);
              } catch (error) {
                if (!(error instanceof RequestError && error.status === 401)) {
                  showToast(error instanceof Error ? error.message : "没记上，再试一次");
                }
              }
            }}
          />
        )}
        {view === "卡路里" && (
          <CaloriesPage
            data={data}
            current={current}
            other={other}
            myKind={myKind}
            otherKind={otherKind}
            today={today}
            onAdd={() => setSheet({ type: "calorie" })}
            onQuick={async (food) => {
              try {
                await api("/api/calories", {
                  method: "POST",
                  body: JSON.stringify({ food_name: food.name, calories: food.calories, meal_type: food.default_meal, local_date: today, occurred_at: new Date().toISOString() }),
                });
                showToast(`记下 ${food.emoji} ${food.name} 啦，好满足～`);
                if (navigator.vibrate) navigator.vibrate(18);
                await loadData(today);
              } catch (error) {
                if (!(error instanceof RequestError && error.status === 401)) {
                  showToast(error instanceof Error ? error.message : "没记上，再试一次");
                }
              }
            }}
            onEdit={(item) => setSheet({ type: "calorie", item })}
            onDelete={(id) => void remove("calories", id)}
          />
        )}
        {view === "体重" && (
          <BodyPage
            data={data}
            current={current}
            other={other}
            myKind={myKind}
            otherKind={otherKind}
            today={today}
            onAdd={(item) => setSheet({ type: "weight", item })}
            onDelete={(id) => void remove("weights", id)}
            onApplyGoal={async (target) => {
              try {
                await api("/api/settings", {
                  method: "PATCH",
                  body: JSON.stringify({
                    calorie_goal: target,
                    daily_budget: current.daily_budget_cents / 100,
                    weekly_budget: current.weekly_budget_cents / 100,
                    monthly_budget: current.monthly_budget_cents / 100,
                  }),
                });
                showToast(`每日目标改成 ${target.toLocaleString()} 千卡啦 🎯`);
                await loadData(today);
              } catch (error) {
                if (!(error instanceof RequestError && error.status === 401)) {
                  showToast(error instanceof Error ? error.message : "没改成功，再试一次");
                }
              }
            }}
          />
        )}
        {view === "记账" && (
          <ExpensesPage
            data={data}
            current={current}
            other={other}
            today={today}
            settlements={mySettlements}
            onAdd={() => setSheet({ type: "expense" })}
            onEdit={(item) => setSheet({ type: "expense", item })}
            onDelete={(id) => void remove("expenses", id)}
            onOpenSettle={() => setSettleOpen(true)}
            onResettle={(date) => void settleDay(date)}
            onUndoSettle={(date) => void removeSettlement(date)}
          />
        )}
        {view === "设置" && (
          <SettingsPage
            data={data}
            current={current}
            today={today}
            myKind={myKind}
            onSaved={async (message) => {
              showToast(message);
              await loadData(today);
            }}
            onFood={(item) => setSheet({ type: "food", item })}
            onDeleteFood={(id) => void remove("foods", id)}
            onLogout={async () => {
              await endSession("下次见啦，随时回来记一笔 🐻🐰");
            }}
          />
        )}
      </section>

      <nav className="bottom-nav" aria-label="主要导航">
        {NAV.map((item) => (
          <button key={item.name} className={view === item.name ? "active" : ""} onClick={() => setView(item.name)}>
            <span>{item.icon}</span>{item.name}
          </button>
        ))}
      </nav>

      {sheet && (
        <EntrySheet
          sheet={sheet}
          today={today}
          me={current}
          onClose={() => setSheet(null)}
          onSaved={async (message) => {
            setSheet(null);
            showToast(message);
            if (navigator.vibrate) navigator.vibrate(18);
            await loadData(today);
          }}
        />
      )}

      {settleOpen && (
        <SettleSheet
          today={today}
          settlements={mySettlements}
          onClose={() => setSettleOpen(false)}
          onSettle={async (date) => {
            setSettleOpen(false);
            await settleDay(date);
          }}
          onUndo={async (date) => {
            setSettleOpen(false);
            await removeSettlement(date);
          }}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
      {celebration && (
        <CelebrationOverlay
          celebration={celebration}
          onClose={() => setCelebration(null)}
          data={data}
          current={current}
          today={today}
        />
      )}
    </main>
  );
}

/* ------------------------------------ 各个页面 ---------------------------------- */

function LoadingScreen({ compact = false }: { compact?: boolean }) {
  return (
    <main className="loading-screen">
      <img className={`loader-mascot ${compact ? "compact" : ""}`} src="/mascots/hug.webp" alt="抱在一起的小熊和小兔" />
      <p>正在把今天的小日子装进来…</p>
    </main>
  );
}

function LoginScreen({ profiles, notice, onLogin }: {
  profiles: User[]; notice: string; onLogin: (userId: number, pin: string) => Promise<void>;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // 没主动选之前默认第一只小动物，避免用 effect 补状态
  const selected = picked ?? profiles[0]?.id ?? null;
  const setSelected = setPicked;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || pin.length !== 4) return setError("先选一只小动物，再输入 4 位 PIN 哦");
    setBusy(true);
    setError("");
    try {
      await onLogin(selected, pin);
    } catch (reason) {
      setPin("");
      setError(reason instanceof Error ? reason.message : "登录没成功，再试一次");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-orbit orbit-one">🍓</div>
      <div className="login-orbit orbit-two">🧸</div>
      <div className="login-orbit orbit-three">☁️</div>
      <section className="login-card">
        <div className="brand-mark">
          <Mascot kind="bear" size={44} className="brand-bear" />
          <Mascot kind="bunny" size={44} className="brand-bunny" />
        </div>
        <p className="eyebrow">二人食光 · 甜甜记账</p>
        <h1>今天是谁来记录？</h1>
        <img className="login-hero-img" src="/mascots/couple.webp" alt="一起吃面的小熊和小兔" />
        <p className="login-subtitle">吃得开心，花得明白，一起把日子过得软软的。</p>
        {notice && <p className="login-notice">{notice}</p>}
        <form onSubmit={submit}>
          <div className="profile-picker">
            {profiles.map((profile, index) => (
              <button
                type="button"
                key={profile.id}
                className={selected === profile.id ? "selected" : ""}
                style={{ "--profile-color": profile.theme_color } as React.CSSProperties}
                onClick={() => {
                  setSelected(profile.id);
                  setPin("");
                }}
              >
                <span className="profile-emoji">
                  <Mascot kind={index === 0 ? "bear" : "bunny"} color={profile.theme_color} size={54} />
                </span>
                <strong>{profile.name}</strong>
                <small>{selected === profile.id ? "就是我 ♡" : "选我登录"}</small>
              </button>
            ))}
          </div>
          <label className="pin-label">
            <span>输入你的 4 位 PIN</span>
            <input
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 4))}
              inputMode="numeric"
              autoComplete="current-password"
              type="password"
              maxLength={4}
              placeholder="••••"
              aria-label="四位 PIN"
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button login-button" disabled={busy || pin.length !== 4}>
            {busy ? "正在开门…" : "进入我们的小世界 →"}
          </button>
        </form>
        <p className="login-footnote">🔒 没有注册入口，只有你们两个人</p>
      </section>
    </main>
  );
}

function Dashboard({ data, current, other, myKind, otherKind, today, refreshing, onSettle, onOpenSettle, onAddCalorie, onAddExpense, onLeave }: {
  data: AppData; current: User; other: User; myKind: MascotKind; otherKind: MascotKind; today: string; refreshing: boolean;
  onSettle: () => void; onOpenSettle: () => void; onAddCalorie: () => void; onAddExpense: () => void;
  onLeave: (date: string, kind: "全天" | "半天" | null) => Promise<void>;
}) {
  const [now, setNow] = useState(() => Date.now());
  const [leaveBusy, setLeaveBusy] = useState(false);

  const cycleStart = monthStart(today);
  const cycleDays = new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 0).getDate();
  const todayRow = scheduleFor(data.schedule, current.id, isoWeekday(today));
  const todayLeave = leaveOn(data.leaves, current.id, today);
  const todayScheduled = scheduledHours(current, data.schedule, data.leaves, today);
  const workedSeconds = workedSecondsOn(current, data.schedule, data.leaves, today, now);
  const todayEarnedCents = earnedTodayCents(current, data.schedule, data.leaves, cycleDays, now);
  const cycleEarnedCents = earnedCycleCents(current, data.schedule, data.leaves, cycleStart, cycleDays, now);
  const cycleWorkedSeconds = workedSecondsInRange(current, data.schedule, data.leaves, cycleStart, today, now);
  const isRestDay = !todayRow;
  const workDone = todayScheduled > 0 && workedSeconds >= todayScheduled * 3600 - 1;
  const otherScheduled = scheduledHours(other, data.schedule, data.leaves, today);
  const otherWorked = workedSecondsOn(other, data.schedule, data.leaves, today, now);
  const otherWorking = otherScheduled > 0 && otherWorked > 0 && otherWorked < otherScheduled * 3600;

  /* 🔴 只在**还在上班**的时候每秒跳；下班后数字本来就不动了，
     再每秒重渲染只是白耗电。不上班时留一个 30 秒的慢拍，
     好让 09:00 一到卡片能自己翻面。 */
  const stillWorking = todayScheduled > 0 && workedSeconds < todayScheduled * 3600;
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), stillWorking ? 1000 : 30000);
    return () => clearInterval(timer);
  }, [stillWorking]);

  const workStatus = todayLeave
    ? `今天请假（${todayLeave.kind}）`
    : isRestDay
      ? "今天休息"
      : workDone
        ? "今天的班上完了"
        : workedSeconds > 0
          ? `上班中 · ${todayRow?.start_time ?? ""}–${todayRow?.end_time ?? ""}`
          : `今天 ${todayRow?.start_time ?? ""} 上班`;

  const [comparePeriod, setComparePeriod] = useState<"今天" | "本周">("今天");
  const [chartMetric, setChartMetric] = useState<"卡路里" | "消费">("卡路里");
  const [chartMode, setChartMode] = useState<"自己" | "对方" | "共同">("自己");
  const weekStart = mondayOf(today);
  const thisMonth = monthStart(today);
  const todayCalories = calorieTotal(data.calories, current.id, today);
  const todaySpend = periodExpenses(data.expenses, current.id, today, today);
  const caloriePercent = clampPercent(todayCalories, current.calorie_goal);
  const spendPercent = clampPercent(todaySpend, current.daily_budget_cents);
  const caloriesOver = todayCalories > current.calorie_goal;
  const budgetOver = todaySpend > current.daily_budget_cents;
  const mood: MascotMood = caloriesOver && budgetOver ? "sleepy" : !budgetOver && !caloriesOver ? "cheer" : "wink";
  const mascotCopy = caloriesOver && budgetOver
    ? "今天走了豪爽路线，明天再稳稳接住"
    : !budgetOver && !caloriesOver
      ? "双双在线，今天状态很会拿捏"
      : !budgetOver
        ? "钱包笑得很灿烂，继续稳住"
        : "吃得刚刚好，钱包只是伸了个懒腰";
  const days = Array.from({ length: 7 }, (_, index) => addDays(today, index - 6));
  const chartValues = days.map((date) => {
    if (chartMetric === "卡路里") {
      if (chartMode === "自己") return calorieTotal(data.calories, current.id, date);
      if (chartMode === "对方") return calorieTotal(data.calories, other.id, date);
      return calorieTotal(data.calories, current.id, date) + calorieTotal(data.calories, other.id, date);
    }
    if (chartMode === "自己") return periodExpenses(data.expenses, current.id, date, date) / 100;
    if (chartMode === "对方") return periodExpenses(data.expenses, other.id, date, date) / 100;
    return data.expenses.filter((item) => item.local_date === date && item.scope === "共同").reduce((sum, item) => sum + item.amount_cents / 100, 0);
  });
  const maxChart = Math.max(...chartValues, 1);
  const compareFrom = comparePeriod === "今天" ? today : weekStart;
  const compareCalories = [current, other].map((user) => calorieTotal(data.calories, user.id, compareFrom, today));
  const compareSpend = [current, other].map((user) => periodExpenses(data.expenses, user.id, compareFrom, today));
  const maxCompareCalories = Math.max(...compareCalories, 1);
  const maxCompareSpend = Math.max(...compareSpend, 1);
  const monthExpenses = data.expenses.filter((item) => item.local_date >= thisMonth && item.local_date <= today);
  const categoryTotals = CATEGORIES
    .map((category) => ({ category, total: monthExpenses.filter((item) => item.category === category).reduce((sum, item) => sum + item.amount_cents, 0) }))
    .sort((a, b) => b.total - a.total);
  const foodCounts = new Map<string, number>();
  data.calories.filter((item) => item.local_date >= thisMonth).forEach((item) => foodCounts.set(item.food_name, (foodCounts.get(item.food_name) ?? 0) + 1));
  const favoriteFood = [...foodCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "等你们开吃";
  const sharedTotal = monthExpenses.filter((item) => item.scope === "共同").reduce((sum, item) => sum + item.amount_cents, 0);
  const monthlySaved = data.settlements
    .filter((item) => item.user_id === current.id && item.local_date >= thisMonth && item.saved_cents > 0)
    .reduce((sum, item) => sum + item.saved_cents, 0);
  const settledToday = data.settlements.some((item) => item.user_id === current.id && item.local_date === today);

  return (
    <div className={`stack ${refreshing ? "soft-loading" : ""}`}>
      <section className="status-card">
        <PawDeco className="corner-paw" />
        <div className="mascot-bubble">
          <Mascot kind={myKind} color={current.theme_color} mood={mood} size={62} />
        </div>
        <div>
          <span className="status-pill">今天的小心情</span>
          <h2>{mascotCopy}</h2>
          <p>你们的小日子，正在认真发光 ✨</p>
        </div>
      </section>

      <section className="card shift-card">
        <div className="section-title">
          <div>
            <p className="eyebrow">{workStatus}</p>
            <h2>{hasRate(current) ? "工钱一直在走" : "今天的工作"}</h2>
          </div>
          <span className={workedSeconds > 0 && !workDone ? "shift-spin" : ""}>
            {todayLeave ? "🏖️" : isRestDay ? "🛋️" : workDone ? "🌙" : "⏱️"}
          </span>
        </div>

        <div className="shift-figures">
          <div>
            <small>今天工作了</small>
            <b>{clockText(workedSeconds)}</b>
          </div>
          <div className="shift-earned">
            <small>{hasRate(current) && !stillWorking && todayScheduled > 0 ? "今天赚满了" : "今天赚了"}</small>
            <b>{hasRate(current) ? rmLive(todayEarnedCents) : "—"}</b>
          </div>
        </div>

        {hasRate(current) ? (
          <p className="hint-note shift-hint">
            月薪买断的是<b>整个月</b>，所以周末和假期也照发。
            工作日的钱在上班那几个钟头里慢慢到账，<b>下班就拿满</b>；休息日直接算满。
            本月已赚 <b>{rmLive(cycleEarnedCents)}</b> · 已工作 {Math.round(cycleWorkedSeconds / 3600)} 小时。
          </p>
        ) : (
          <p className="hint-note shift-hint">填了「设置 → 真实时薪」，钱才会开始跳 ⏳</p>
        )}

        <button
          className={todayLeave ? "secondary-button shift-button" : "primary-button shift-button"}
          disabled={leaveBusy}
          onClick={async () => {
            setLeaveBusy(true);
            try { await onLeave(today, todayLeave ? null : "全天"); } finally { setLeaveBusy(false); }
          }}
        >
          {leaveBusy ? "稍等一下…" : todayLeave ? "取消请假，今天照常上班" : "今天请假 🏖️"}
        </button>
        <p className="shift-count">不用打卡，按班表自动算。只有请假才要按一下。</p>

        {otherWorking && (
          <p className="shift-other">{other.avatar} {other.name} 现在也在上班</p>
        )}
      </section>

      <section className="overview-grid">
        <article className="card calorie-overview">
          <div className="card-heading">
            <div><p className="eyebrow">今日卡路里</p><h3>{todayCalories.toLocaleString()} <small>目标 {current.calorie_goal.toLocaleString()} 千卡</small></h3></div>
            <span>🍡</span>
          </div>
          <div className="calorie-ring" style={{ "--ring-progress": `${caloriePercent * 3.6}deg` } as React.CSSProperties}>
            <div><strong>{caloriePercent}%</strong><span>已吃</span></div>
          </div>
          <p className="gentle-note">{caloriesOver ? `多吃了 ${(todayCalories - current.calorie_goal).toLocaleString()} 千卡，开心最重要` : `还能安排 ${(current.calorie_goal - todayCalories).toLocaleString()} 千卡`}</p>
        </article>
        <article className="card budget-overview">
          <div className="card-heading">
            <div><p className="eyebrow">今日消费</p><h3>{rm(todaySpend)} <small>预算 {rm(current.daily_budget_cents)}</small></h3></div>
            <span>🪙</span>
          </div>
          <div className="big-balance">
            <span>还剩</span>
            <strong className={budgetOver ? "overspent" : ""}>{budgetOver ? `超了 ${rm(todaySpend - current.daily_budget_cents)}` : rm(current.daily_budget_cents - todaySpend)}</strong>
          </div>
          <div className="progress-track"><span style={{ width: `${spendPercent}%` }} /></div>
          <p className="gentle-note">本月结算已省下 <b>{rm(monthlySaved)}</b></p>
          <button className="settle-button" onClick={onSettle}>{settledToday ? "🔁 重新结算今天" : "🎉 结算今天"}</button>
          <button className="link-button" onClick={onOpenSettle}>补结算 / 撤销其它日子 →</button>
        </article>
      </section>

      <section className="quick-actions">
        <button className="bounce-button blue" onClick={onAddCalorie}><span>＋</span><div><strong>记一口</strong><small>添加卡路里</small></div></button>
        <button className="bounce-button coral" onClick={onAddExpense}><span>＋</span><div><strong>记一笔</strong><small>添加消费</small></div></button>
      </section>

      <section className="card streak-card">
        <div className="section-title"><div><p className="eyebrow">连续达标</p><h2>你们很会坚持耶</h2></div><span className="flame">🔥</span></div>
        <div className="streak-row">
          <Streak person={`${current.avatar} ${current.name}`} count={streakFor(data.settlements, current.id, today)} color={current.theme_color} />
          <Streak person={`${other.avatar} ${other.name}`} count={streakFor(data.settlements, other.id, today)} color={other.theme_color} />
          <Streak person="💞 两个人" count={coupleStreak(data.settlements, data.users, today)} color="#C6AEE8" />
        </div>
      </section>

      <section className="card comparison-card">
        <div className="section-title">
          <div><p className="eyebrow">甜蜜对比</p><h2>今天谁更稳？</h2></div>
          <Segmented options={["今天", "本周"]} value={comparePeriod} onChange={(value) => setComparePeriod(value as "今天" | "本周")} />
        </div>
        <div className="versus-grid">
          {[current, other].map((user, index) => (
            <div className="versus-person" key={user.id}>
              <div className="versus-avatar" style={{ background: `${user.theme_color}26` }}>
                <Mascot kind={index === 0 ? myKind : otherKind} color={user.theme_color} size={40} />
              </div>
              <strong>{user.name}</strong>
              <div className="metric-line"><span>卡路里</span><b>{compareCalories[index].toLocaleString()}</b></div>
              <div className="mini-track"><i style={{ width: `${Math.round(compareCalories[index] / maxCompareCalories * 100)}%`, background: user.theme_color }} /></div>
              <div className="metric-line"><span>消费</span><b>{rm(compareSpend[index])}</b></div>
              <div className="mini-track"><i style={{ width: `${Math.round(compareSpend[index] / maxCompareSpend * 100)}%`, background: user.theme_color }} /></div>
              {/* 各人按各人的时薪算 —— 同样的钱对两个人不是同样的时间 */}
              {hasRate(user) && (
                <div className="metric-line life"><span>换成人生</span><b>{lifeText(hoursOfCents(compareSpend[index], user), user)}</b></div>
              )}
            </div>
          ))}
          <span className="versus-badge">♡</span>
        </div>
        <p className="competition-copy">
          {compareSpend[0] === compareSpend[1]
            ? "钱包同步率 100%，很有默契 💕"
            : compareSpend[0] < compareSpend[1]
              ? `${current.name} 暂时领先省钱赛道 🏁`
              : `${other.name} 暂时领先省钱赛道 🏁`}
        </p>
      </section>

      <section className="card chart-card">
        <div className="section-title chart-title">
          <div><p className="eyebrow">近 7 天趋势</p><h2>{chartMetric}小波浪</h2></div>
          <Segmented options={["卡路里", "消费"]} value={chartMetric} onChange={(value) => setChartMetric(value as "卡路里" | "消费")} />
        </div>
        <div className="chart-mode"><Segmented options={["自己", "对方", "共同"]} value={chartMode} onChange={(value) => setChartMode(value as "自己" | "对方" | "共同")} /></div>
        <div className="bar-chart" aria-label={`近七天${chartMetric}柱状图`}>
          {days.map((date, index) => (
            <div className="bar-column" key={date}>
              <span className="bar-value">{chartValues[index] > 0 ? Math.round(chartValues[index]).toLocaleString() : ""}</span>
              <div className="bar-rail">
                <i style={{ height: `${Math.max(chartValues[index] > 0 ? 8 : 2, chartValues[index] / maxChart * 100)}%`, background: chartMode === "对方" ? other.theme_color : chartMode === "共同" ? "#C6AEE8" : current.theme_color }} />
              </div>
              <b>{date === today ? "今天" : `周${dayLabel(date)}`}</b><small>{dateLabel(date)}</small>
            </div>
          ))}
        </div>
      </section>

      <section className="fun-stats">
        <article><span>🔥</span><small>本月最烧钱</small><strong>{categoryTotals[0]?.total ? categoryTotals[0].category : "还没有"}</strong><em>{categoryTotals[0]?.total ? rm(categoryTotals[0].total) : "RM0.00"}</em></article>
        <article><span>😋</span><small>最常吃的</small><strong>{favoriteFood}</strong><em>真爱不会腻</em></article>
        <article><span>💑</span><small>共同支出</small><strong>{rm(sharedTotal)}</strong><em>一起花也开心</em></article>
      </section>
    </div>
  );
}

function Streak({ person, count, color }: { person: string; count: number; color: string }) {
  return (
    <div className="streak-item">
      <span style={{ background: `${color}2e`, color: "#6B5A52" }}>{count}</span>
      <strong>{person}</strong>
      <small>连续 {count} 天</small>
    </div>
  );
}

function Segmented({ options, value, onChange }: { options: string[]; value: string; onChange: (value: string) => void }) {
  return (
    <div className="segmented">
      {options.map((option) => (
        <button type="button" key={option} className={value === option ? "active" : ""} onClick={() => onChange(option)}>{option}</button>
      ))}
    </div>
  );
}

function CaloriesPage({ data, current, other, myKind, otherKind, today, onAdd, onQuick, onEdit, onDelete }: {
  data: AppData; current: User; other: User; myKind: MascotKind; otherKind: MascotKind; today: string;
  onAdd: () => void; onQuick: (food: CommonFood) => Promise<void>; onEdit: (item: CalorieEntry) => void; onDelete: (id: number) => void;
}) {
  const [watching, setWatching] = useState(current.id);
  const watchedUser = watching === current.id ? current : other;
  const watchedKind = watching === current.id ? myKind : otherKind;
  const entries = data.calories.filter((item) => item.user_id === watching && item.local_date === today);
  const total = entries.reduce((sum, item) => sum + item.calories, 0);
  const foods = data.foods.filter((food) => food.user_id === current.id);
  const grouped = MEALS.map((meal) => ({ meal, items: entries.filter((item) => item.meal_type === meal) })).filter((group) => group.items.length);

  return (
    <div className="stack page-stack">
      <div className="person-tabs">
        {[current, other].map((user, index) => (
          <button key={user.id} className={watching === user.id ? "active" : ""} onClick={() => setWatching(user.id)}>
            <Mascot kind={index === 0 ? myKind : otherKind} color={user.theme_color} size={26} />
            {user.id === current.id ? "我的今天" : "看看对方"}
          </button>
        ))}
      </div>

      <section className="card calorie-hero">
        <PawDeco className="corner-paw" />
        <div>
          <p className="eyebrow">{watchedUser.name} · 今日摄入</p>
          <h2>{total.toLocaleString()} <small>千卡</small></h2>
          <p>{total > watchedUser.calorie_goal ? "今天吃得很满足，身体也收到快乐了" : `距离目标还有 ${(watchedUser.calorie_goal - total).toLocaleString()} 千卡`}</p>
        </div>
        <div className="hero-mascot">
          <Mascot kind={watchedKind} color={watchedUser.theme_color} mood={total > watchedUser.calorie_goal ? "wink" : "happy"} size={64} />
          <div className="mini-ring" style={{ "--ring-progress": `${clampPercent(total, watchedUser.calorie_goal) * 3.6}deg`, "--ring-color": watchedUser.theme_color } as React.CSSProperties}>
            <b>{clampPercent(total, watchedUser.calorie_goal)}%</b>
          </div>
        </div>
      </section>

      {watching === current.id && (
        <section>
          <div className="section-title"><div><p className="eyebrow">一按就记</p><h2>常吃食物</h2></div><button className="text-button" onClick={() => onAdd()}>手动添加 ＋</button></div>
          {foods.length === 0 ? (
            <EmptyState kind={myKind} title="还没有常吃的" copy="把最爱的那几样加进来，以后一按就记好。" />
          ) : (
            <div className="food-carousel">
              {foods.map((food) => (
                <button key={food.id} onClick={() => void onQuick(food)}>
                  <span>{food.emoji}</span>
                  <strong>{food.name}</strong>
                  <small>{food.calories} 千卡</small>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="meal-list">
        <div className="section-title">
          <div><p className="eyebrow">今日记录</p><h2>{watching === current.id ? "我吃了这些" : `${other.name} 吃了这些 😏`}</h2></div>
          <span className="record-count">{entries.length} 项</span>
        </div>
        {grouped.length === 0 ? (
          <EmptyState kind={watchedKind} title="今天还没开吃" copy={watching === current.id ? "第一口，值得认真记下来。" : "对方还没记录，再等等看吧。"} />
        ) : grouped.map((group) => (
          <div className="meal-group" key={group.meal}>
            <h3><span>{MEAL_ICON[group.meal] ?? "🍽️"}</span>{group.meal}<small>{group.items.reduce((sum, item) => sum + item.calories, 0)} 千卡</small></h3>
            {group.items.map((item) => (
              <RecordRow
                key={item.id}
                icon="🍴"
                title={item.food_name}
                meta={new Date(item.occurred_at).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}
                value={`${item.calories} 千卡`}
                color={watchedUser.theme_color}
                editable={watching === current.id}
                onEdit={() => onEdit(item)}
                onDelete={() => onDelete(item.id)}
              />
            ))}
          </div>
        ))}
      </section>
      {watching === current.id && <button className="floating-add" onClick={() => onAdd()}><span>＋</span> 记录食物</button>}
    </div>
  );
}

function WeightSpark({ points, color }: { points: Array<{ date: string; grams: number }>; color: string }) {
  if (points.length < 2) return null;
  const W = 320;
  const H = 110;
  const PAD = 12;
  const values = points.map((point) => point.grams);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 500); // 至少给 0.5 公斤的纵向余量，免得小波动被放大成悬崖
  const mid = (min + max) / 2;
  const low = mid - span / 2;
  const x = (index: number) => PAD + (index * (W - PAD * 2)) / (points.length - 1);
  const y = (grams: number) => H - PAD - ((grams - low) / span) * (H - PAD * 2);
  const line = points.map((point, index) => `${x(index).toFixed(1)},${y(point.grams).toFixed(1)}`).join(" ");
  const area = `${PAD},${H - PAD} ${line} ${(W - PAD).toFixed(1)},${H - PAD}`;
  return (
    <svg className="weight-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="体重趋势">
      <polygon points={area} fill={color} opacity=".14" />
      <polyline points={line} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((point, index) => (
        <circle key={point.date} cx={x(index)} cy={y(point.grams)} r={index === points.length - 1 ? 5 : 3} fill="#fff" stroke={color} strokeWidth="2.5" />
      ))}
    </svg>
  );
}

function BodyPage({ data, current, other, myKind, otherKind, today, onAdd, onDelete, onApplyGoal }: {
  data: AppData; current: User; other: User; myKind: MascotKind; otherKind: MascotKind; today: string;
  onAdd: (item?: WeightEntry) => void; onDelete: (id: number) => void; onApplyGoal: (target: number) => Promise<void>;
}) {
  const [watching, setWatching] = useState(current.id);
  const isMe = watching === current.id;
  const person = isMe ? current : other;
  const kind = isMe ? myKind : otherKind;
  const records = weightsOf(data.weights, person.id);
  const latest = records[0];
  const previous = records[1];
  const weekAgo = records.find((item) => item.local_date <= addDays(today, -7));
  const bmi = latest ? bmiOf(latest.weight_grams, person.height_cm) : 0;
  const band = bmi ? bmiBand(bmi) : null;
  const range = person.height_cm ? healthyWeightRange(person.height_cm) : null;
  const plan = latest ? dailyPlan(person, latest.weight_grams, today) : null;
  const chartPoints = records.slice(0, 30).reverse().map((item) => ({ date: item.local_date, grams: item.weight_grams }));
  const missingProfile = !person.height_cm || !person.sex || !person.birth_year;
  const targetGrams = person.target_weight_grams;
  const toGo = latest && targetGrams ? latest.weight_grams - targetGrams : 0;
  const weeksToGo = toGo > 0 && person.weekly_loss_grams > 0 ? Math.ceil(toGo / person.weekly_loss_grams) : 0;
  const targetTooLow = Boolean(targetGrams && range && targetGrams < range[0]);
  // BMI 刻度条：把 15~35 映射到 0~100%
  const markerPercent = bmi ? Math.min(100, Math.max(0, ((bmi - 15) / 20) * 100)) : 0;

  return (
    <div className="stack page-stack">
      <div className="person-tabs">
        {[current, other].map((user, index) => (
          <button key={user.id} className={watching === user.id ? "active" : ""} onClick={() => setWatching(user.id)}>
            <Mascot kind={index === 0 ? myKind : otherKind} color={user.theme_color} size={26} />
            {user.id === current.id ? "我的体重" : "看看对方"}
          </button>
        ))}
      </div>

      <section className="card weight-hero" style={{ "--hero-color": person.theme_color } as React.CSSProperties}>
        <PawDeco className="corner-paw" />
        <div>
          <p className="eyebrow">{person.name} · 最近一次</p>
          {latest ? (
            <>
              <h2>{(latest.weight_grams / 1000).toFixed(1)} <small>公斤</small></h2>
              <p className="weight-meta">
                {friendlyDate(latest.local_date, today)}
                {previous && (
                  <span className={latest.weight_grams <= previous.weight_grams ? "down" : "up"}>
                    {latest.weight_grams === previous.weight_grams
                      ? " · 和上次一样"
                      : ` · 比上次${latest.weight_grams < previous.weight_grams ? "轻" : "重"} ${((Math.abs(latest.weight_grams - previous.weight_grams)) / 1000).toFixed(1)} 公斤`}
                  </span>
                )}
              </p>
              {weekAgo && weekAgo.id !== latest.id && (
                <p className="weight-meta subtle">
                  比一周前{latest.weight_grams < weekAgo.weight_grams ? "轻" : latest.weight_grams > weekAgo.weight_grams ? "重" : "持平"}
                  {latest.weight_grams !== weekAgo.weight_grams && ` ${(Math.abs(latest.weight_grams - weekAgo.weight_grams) / 1000).toFixed(1)} 公斤`}
                </p>
              )}
            </>
          ) : (
            <>
              <h2 className="weight-empty">还没记过</h2>
              <p className="weight-meta">{isMe ? "记下第一笔，就能看到趋势啦" : "对方还没有记录"}</p>
            </>
          )}
        </div>
        <div className="hero-mascot">
          <Mascot kind={kind} color={person.theme_color} mood={band?.tone === "good" ? "cheer" : "happy"} size={64} />
        </div>
      </section>

      {chartPoints.length >= 2 && (
        <section className="card chart-card">
          <div className="section-title"><div><p className="eyebrow">最近 {chartPoints.length} 次</p><h2>体重小曲线</h2></div><span>📈</span></div>
          <WeightSpark points={chartPoints} color={person.theme_color} />
          <div className="spark-legend">
            <span>{friendlyDate(chartPoints[0].date, today)}</span>
            <span>{friendlyDate(chartPoints[chartPoints.length - 1].date, today)}</span>
          </div>
        </section>
      )}

      {latest && band && person.height_cm ? (
        <section className="card bmi-card">
          <div className="section-title"><div><p className="eyebrow">身体质量指数</p><h2>BMI {bmi.toFixed(1)}</h2></div><span className={`bmi-chip ${band.tone}`}>{band.label}</span></div>
          <div className="bmi-scale" aria-hidden="true">
            <i style={{ left: `${markerPercent}%` }} />
          </div>
          <div className="bmi-ticks"><span>偏瘦</span><span>正常</span><span>超重</span><span>肥胖</span></div>
          <p className="gentle-note">{band.copy}</p>
          {range && (
            <p className="gentle-note">
              以 {person.height_cm} 公分来算，健康体重大约是 <b>{kgText(range[0])} ~ {kgText(range[1])}</b>
            </p>
          )}
          <p className="hint-note">用的是亚洲标准（马来西亚卫生部那套）。BMI 看不出肌肉和体脂的区别，当个大致参考就好，真要看身体状况还是问医生 🩺</p>
        </section>
      ) : null}

      {isMe && (
        missingProfile ? (
          <section className="card settings-card">
            <div className="section-title"><div><p className="eyebrow">还差一点点</p><h2>补上身体资料</h2></div><span>📏</span></div>
            <p className="gentle-note">填了身高、性别和出生年份，我就能帮你算 BMI 和「一天该吃多少」。这些只用来算数，不会显示给对方看到具体数字以外的东西。</p>
            <p className="hint-note">去「设置 → 身体资料」填一下就好 🎀</p>
          </section>
        ) : plan ? (
          <section className="card plan-card">
            <div className="section-title"><div><p className="eyebrow">每日建议</p><h2>今天大概该吃</h2></div><span>🎯</span></div>
            <div className="plan-hero">
              <strong>{plan.target.toLocaleString()}</strong>
              <span>千卡 / 天</span>
            </div>
            <div className="plan-rows">
              <div><span>基础代谢</span><b>{plan.bmr.toLocaleString()} 千卡</b></div>
              <div><span>每日消耗（含活动）</span><b>{plan.tdee.toLocaleString()} 千卡</b></div>
              <div><span>热量缺口</span><b>{plan.deficit > 0 ? `-${plan.deficit.toLocaleString()} 千卡` : "不制造缺口"}</b></div>
            </div>
            {plan.underweight && (
              <p className="plan-warn">你现在已经偏瘦了，所以这里没有帮你减，按维持来算的 🌱</p>
            )}
            {plan.capped && (
              <p className="plan-warn">照你选的速度会算到比 {plan.floor.toLocaleString()} 千卡还低，已经帮你托到这条线上了。吃太少反而会掉肌肉、代谢变慢 🧸</p>
            )}
            {targetTooLow && (
              <p className="plan-warn">你设的目标体重低于健康区间下限，要不要再往上调一点点？</p>
            )}
            {weeksToGo > 0 && !targetTooLow && (
              <p className="gentle-note">离目标还有 <b>{kgText(toGo)}</b>，照现在的速度大约 <b>{weeksToGo} 周</b>（{friendlyDate(addDays(today, weeksToGo * 7), today)}）。</p>
            )}
            {current.calorie_goal === plan.target ? (
              <p className="plan-applied">✓ 已经是你现在的每日目标了</p>
            ) : (
              <button className="primary-button" onClick={() => void onApplyGoal(plan.target)}>
                用这个当我的每日目标（现在是 {current.calorie_goal.toLocaleString()}）
              </button>
            )}
            <p className="hint-note">公式是 Mifflin-St Jeor + 活动系数，只是估算，每个人代谢都不一样。真要认真减，配合医生或营养师会稳很多 💛</p>
          </section>
        ) : null
      )}

      <section className="meal-list">
        <div className="section-title">
          <div><p className="eyebrow">记录</p><h2>{isMe ? "我的体重记录" : `${other.name} 的记录`}</h2></div>
          <span className="record-count">{records.length} 次</span>
        </div>
        {records.length === 0 ? (
          <EmptyState kind={kind} title="还没有记录" copy={isMe ? "每天同一个时间称，数字才好比较。" : "对方还没有记录哦。"} />
        ) : (
          <div className="expense-list">
            {records.slice(0, 30).map((item, index) => {
              const prev = records[index + 1];
              const delta = prev ? item.weight_grams - prev.weight_grams : 0;
              return (
                <RecordRow
                  key={item.id}
                  icon={delta === 0 ? "🎈" : delta < 0 ? "🍃" : "🧁"}
                  title={`${(item.weight_grams / 1000).toFixed(1)} 公斤`}
                  meta={`${friendlyDate(item.local_date, today)}${item.note ? ` · ${item.note}` : ""}${prev ? ` · ${delta === 0 ? "持平" : `${delta < 0 ? "↓" : "↑"} ${(Math.abs(delta) / 1000).toFixed(1)}`}` : ""}`}
                  value={person.height_cm ? `BMI ${bmiOf(item.weight_grams, person.height_cm).toFixed(1)}` : ""}
                  color={person.theme_color}
                  editable={isMe}
                  onEdit={() => onAdd(item)}
                  onDelete={() => onDelete(item.id)}
                />
              );
            })}
          </div>
        )}
      </section>
      {isMe && <button className="floating-add" onClick={() => onAdd()}><span>＋</span> 记录体重</button>}
    </div>
  );
}

function ExpensesPage({ data, current, other, today, settlements, onAdd, onEdit, onDelete, onOpenSettle, onResettle, onUndoSettle }: {
  data: AppData; current: User; other: User; today: string; settlements: Settlement[];
  onAdd: () => void; onEdit: (item: Expense) => void; onDelete: (id: number) => void;
  onOpenSettle: () => void; onResettle: (date: string) => void; onUndoSettle: (date: string) => void;
}) {
  const [filter, setFilter] = useState<"全部" | "个人" | "共同" | "后悔">("全部");
  const start = monthStart(today);
  const monthItems = data.expenses.filter((item) => item.local_date >= start && item.local_date <= today);
  const visible = monthItems.filter((item) =>
    filter === "全部" ? true : filter === "后悔" ? item.sentiment === "后悔" : item.scope === filter);
  const shared = monthItems.filter((item) => item.scope === "共同");
  const sharedTotal = shared.reduce((sum, item) => sum + item.amount_cents, 0);
  const sharedMine = shared.reduce((sum, item) => sum + shareOf(item, current.id), 0);
  const sharedOther = shared.reduce((sum, item) => sum + shareOf(item, other.id), 0);
  const currentPaid = shared.filter((item) => item.payer_user_id === current.id).reduce((sum, item) => sum + item.amount_cents, 0);
  const net = currentPaid - sharedMine;
  const currentMonthSpend = periodExpenses(data.expenses, current.id, start, today);
  const weekStart = mondayOf(today);
  const budgetPeriods = [
    { label: "今天", icon: "☀️", spent: periodExpenses(data.expenses, current.id, today, today), budget: current.daily_budget_cents },
    { label: "本周", icon: "🗓️", spent: periodExpenses(data.expenses, current.id, weekStart, today), budget: current.weekly_budget_cents },
    { label: "本月", icon: "🌙", spent: currentMonthSpend, budget: current.monthly_budget_cents },
  ];
  const recentSettlements = settlements.slice(0, 10);

  /* ── 时间那一层 ──────────────────────────────────────────────────────
     同一笔共同支出，两个人的时薪不同 → 卖掉的生命也不同。
     这里一律按「各自实际负担的那一半」算，跟 AA 口径一致。        */
  const otherMonthSpend = periodExpenses(data.expenses, other.id, start, today);
  const myHours = hoursOfCents(currentMonthSpend, current);
  const otherHours = hoursOfCents(otherMonthSpend, other);
  const bothHaveRate = hasRate(current) && hasRate(other);

  const byCategory = CATEGORIES
    .map((category) => {
      const cents = monthItems
        .filter((item) => item.category === category)
        .reduce((sum, item) => sum + shareOf(item, current.id), 0);
      return { category, cents, hours: hoursOfCents(cents, current) };
    })
    .filter((row) => row.cents > 0)
    .sort((a, b) => b.cents - a.cents);
  const topHours = byCategory.length ? Math.max(...byCategory.map((row) => row.hours)) : 0;

  const regretItems = monthItems.filter((item) => item.sentiment === "后悔");
  const regretCents = regretItems.reduce((sum, item) => sum + shareOf(item, current.id), 0);
  const taggedCents = monthItems
    .filter((item) => item.sentiment)
    .reduce((sum, item) => sum + shareOf(item, current.id), 0);

  return (
    <div className="stack page-stack">
      <section className="ledger-hero">
        <PawDeco className="corner-paw light" />
        <p className="eyebrow">本月我的实际负担</p>
        <h2>{rm(currentMonthSpend)}</h2>
        {hasRate(current) && <p className="hero-life">≈ 你的 {lifeText(myHours, current)}人生</p>}
        <div className="progress-track light"><span style={{ width: `${clampPercent(currentMonthSpend, current.monthly_budget_cents)}%` }} /></div>
        <div className="ledger-hero-row"><span>月预算 {rm(current.monthly_budget_cents)}</span><b>还剩 {rm(Math.max(0, current.monthly_budget_cents - currentMonthSpend))}</b></div>
      </section>

      {!hasRate(current) && (
        <section className="card rate-invite">
          <span className="rate-invite-icon">⏳</span>
          <div>
            <strong>想知道这些钱等于多少人生吗</strong>
            <p>去「设置 → 真实时薪」填一下税后收入和通勤，之后每一笔都会多一行：这笔花掉你几分钟。</p>
          </div>
        </section>
      )}

      {bothHaveRate && (
        <section className="card life-compare">
          <div className="section-title"><div><p className="eyebrow">这个月</p><h2>你们各自卖掉多少生命</h2></div><span>⏳</span></div>
          <div className="life-compare-grid">
            {[{ user: current, cents: currentMonthSpend, hours: myHours, mine: true },
              { user: other, cents: otherMonthSpend, hours: otherHours, mine: false }].map((row) => (
              <article key={row.user.id} style={{ background: `${row.user.theme_color}1f` }}>
                <span className="life-compare-face">{row.user.avatar}</span>
                <p>{row.mine ? "我" : row.user.name}</p>
                <b className="life-strong">{lifeText(row.hours, row.user)}</b>
                <small>{rm(row.cents)}</small>
                <em>时薪 {rm(Math.round(centsPerHour(row.user)))}／小时</em>
              </article>
            ))}
          </div>
          <p className="aa-note">
            {Math.abs(myHours - otherHours) < 0.5
              ? "你们花掉的生命几乎一样多，真的很同步 💞"
              : myHours > otherHours
                ? `同样的日子，你比 ${other.name} 多卖掉 ${lifeText(myHours - otherHours, current)}。`
                : `同样的日子，${other.name} 比你多卖掉 ${lifeText(otherHours - myHours, other)}。`}
          </p>
        </section>
      )}

      {hasRate(current) && byCategory.length > 0 && (
        <section className="card life-rank">
          <div className="section-title"><div><p className="eyebrow">最吃生命的分类</p><h2>钱花在哪，命就花在哪</h2></div><span>🍰</span></div>
          <div className="life-rank-list">
            {byCategory.map((row) => (
              <div className="life-rank-row" key={row.category}>
                <span className="life-rank-icon">{CATEGORY_ICON[row.category] ?? "🧺"}</span>
                <span className="life-rank-name">{row.category}</span>
                <span className="life-rank-track">
                  <i style={{ width: `${topHours > 0 ? Math.max(4, (row.hours / topHours) * 100) : 4}%` }} />
                </span>
                <span className="life-rank-value">
                  <b>{lifeText(row.hours, current)}</b>
                  <small>{rm(row.cents)}</small>
                </span>
              </div>
            ))}
          </div>
          {taggedCents > 0 && (
            <p className="aa-note">
              标成「后悔」的有 {rm(regretCents)}，等于 {lifeText(hoursOfCents(regretCents, current), current)}的人生 😞
            </p>
          )}
        </section>
      )}

      <section className="budget-periods">
        {budgetPeriods.map((period) => {
          const remaining = period.budget - period.spent;
          return (
            <article key={period.label}>
              <div><span>{period.icon}</span><strong>{period.label}</strong></div>
              <small>还剩</small>
              <b className={remaining < 0 ? "negative" : ""}>{remaining < 0 ? `超 ${rm(-remaining)}` : rm(remaining)}</b>
              <div className="mini-track"><i style={{ width: `${clampPercent(period.spent, period.budget)}%` }} /></div>
              <em>{rm(period.spent)} / {rm(period.budget)}</em>
            </article>
          );
        })}
      </section>

      <section className="card aa-card">
        <div className="section-title"><div><p className="eyebrow">平摊小管家</p><h2>这个月谁垫了多少</h2></div><span>🧮</span></div>
        <div className="aa-total">
          <span>共同支出</span>
          <strong>{rm(sharedTotal)}</strong>
          <small>{current.name} 摊 {rm(sharedMine)} · {other.name} 摊 {rm(sharedOther)}</small>
        </div>
        <div className="payer-pills">
          {[current, other].map((user) => {
            const paid = shared.filter((item) => item.payer_user_id === user.id).reduce((sum, item) => sum + item.amount_cents, 0);
            return <div key={user.id}><span>{user.avatar}</span><p>{user.name}<b>垫了 {rm(paid)}</b></p></div>;
          })}
        </div>
        <div className={`settlement-result ${net === 0 ? "even" : ""}`}>
          {net === 0
            ? "刚刚好打平，你们的默契太准了 💞"
            : net > 0
              ? `${current.avatar} ${current.name} 多垫了 ${rm(net)}，${other.name} 转这笔就平啦`
              : `${other.avatar} ${other.name} 多垫了 ${rm(-net)}，你转这笔就平啦`}
        </div>
        <p className="aa-note">单数分（例如 RM0.01）由付款的那一方多承担 1 分，两个人加起来永远刚刚好 🧸</p>
      </section>

      <section className="card settle-history">
        <div className="section-title">
          <div><p className="eyebrow">结算记录</p><h2>我的每日结算</h2></div>
          <button className="text-button" onClick={onOpenSettle}>补结算 ＋</button>
        </div>
        {recentSettlements.length === 0 ? (
          <EmptyState kind="bear" title="还没有结算过" copy="睡前点一下「结算今天」，就能看看今天省了多少。" />
        ) : (
          <div className="settle-list">
            {recentSettlements.map((item) => (
              <div className="settle-row" key={item.local_date}>
                <span className={item.saved_cents >= 0 ? "settle-dot good" : "settle-dot over"}>{item.saved_cents >= 0 ? "🌸" : "🍂"}</span>
                <div className="settle-copy">
                  <strong>{friendlyDate(item.local_date, today)}</strong>
                  <small>花了 {rm(item.spent_cents)} · 预算 {rm(item.budget_cents)}</small>
                </div>
                <b className={item.saved_cents >= 0 ? "" : "over"}>{item.saved_cents >= 0 ? `省下 ${rm(item.saved_cents)}` : `超了 ${rm(-item.saved_cents)}`}</b>
                <div className="settle-row-actions">
                  <button className="undo-button" onClick={() => onResettle(item.local_date)}>重算</button>
                  <button className="undo-button danger" onClick={() => onUndoSettle(item.local_date)}>撤销</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="expense-section">
        <div className="section-title"><div><p className="eyebrow">本月明细</p><h2>每一笔都清清楚楚</h2></div><button className="circle-add" onClick={onAdd}>＋</button></div>
        <Segmented options={["全部", "个人", "共同", "后悔"]} value={filter} onChange={(value) => setFilter(value as "全部" | "个人" | "共同" | "后悔")} />
        <div className="expense-list">
          {visible.length === 0 ? (
            <EmptyState
              kind="bunny"
              title={filter === "后悔" ? "没有后悔的一笔" : "这里还空空的"}
              copy={filter === "后悔" ? "这个月花的每一笔你都觉得值，很好呀 🌈" : "记下第一笔，钱包就有故事了。"}
            />
          ) : visible.map((item) => {
            const payer = data.users.find((user) => user.id === item.payer_user_id) ?? current;
            // 共同支出算「我摊的那一半」的时间 —— 跟 AA 口径一致，写成「我 38 分钟」以免误读成全额。
            const mine = shareOf(item, current.id);
            const life = lifeOf(mine, current);
            return (
              <RecordRow
                key={item.id}
                icon={CATEGORY_ICON[item.category] ?? "🧺"}
                title={item.note || item.category}
                meta={`${item.local_date.slice(5, 7)}/${item.local_date.slice(8, 10)} · ${payer.name} 付款 · ${item.scope}${item.sentiment ? ` · ${SENTIMENT_ICON[item.sentiment]}${item.sentiment}` : ""}`}
                value={`-${rm(item.amount_cents)}`}
                life={life ? (item.scope === "共同" ? `我 ${life}` : life) : null}
                color={payer.theme_color}
                badge={item.note ? item.category : undefined}
                editable={item.payer_user_id === current.id}
                onEdit={() => onEdit(item)}
                onDelete={() => onDelete(item.id)}
              />
            );
          })}
        </div>
      </section>
      <button className="floating-add" onClick={onAdd}><span>＋</span> 记一笔消费</button>
    </div>
  );
}

function SettingsPage({ data, current, today, myKind, onSaved, onFood, onDeleteFood, onLogout }: {
  data: AppData; current: User; today: string; myKind: MascotKind; onSaved: (message: string) => Promise<void>;
  onFood: (item?: CommonFood) => void; onDeleteFood: (id: number) => void; onLogout: () => Promise<void>;
}) {
  const [profile, setProfile] = useState({ name: current.name, avatar: current.avatar, theme_color: current.theme_color });
  const [goals, setGoals] = useState({
    calorie_goal: String(current.calorie_goal),
    daily_budget: String(current.daily_budget_cents / 100),
    weekly_budget: String(current.weekly_budget_cents / 100),
    monthly_budget: String(current.monthly_budget_cents / 100),
  });
  const [pins, setPins] = useState({ current_pin: "", new_pin: "" });
  const [body, setBody] = useState({
    height_cm: current.height_cm ? String(current.height_cm) : "",
    sex: current.sex,
    birth_year: current.birth_year ? String(current.birth_year) : "",
    activity_level: current.activity_level,
    target_weight: current.target_weight_grams ? (current.target_weight_grams / 1000).toFixed(1) : "",
    weekly_loss_grams: current.weekly_loss_grams,
  });
  const [rate, setRate] = useState({
    take_home: current.take_home_cents ? String(current.take_home_cents / 100) : "",
    pay_period: current.pay_period ?? "月",
    blocks: rowsToBlocks([1, 2, 3, 4, 5, 6, 7].map((weekday) => {
      const row = data.schedule.find((item) => item.user_id === current.id && item.weekday === weekday);
      return {
        weekday,
        enabled: row ? !!row.enabled : weekday <= 5,
        start_time: row?.start_time || "09:00",
        end_time: row?.end_time || "18:00",
      };
    })),
    commute_minutes: String(current.commute_minutes ?? 0),
    extra_minutes: String(current.extra_minutes ?? 0),
    time_as_workdays: !!current.time_as_workdays,
  });
  /* 补请假：忘了记的那天要能补回来，所以日期是可选的，不是只能点今天。 */
  const [leaveForm, setLeaveForm] = useState({ local_date: today, kind: "全天" as "全天" | "半天", note: "" });
  const [busy, setBusy] = useState(false);
  const myLeaves = data.leaves
    .filter((item) => item.user_id === current.id)
    .slice()
    .sort((a, b) => (a.local_date < b.local_date ? 1 : -1));
  const foods = data.foods.filter((food) => food.user_id === current.id);
  const isCustomColor = !THEME_PRESETS.some((preset) => preset.value === profile.theme_color.toLowerCase());

  /* 预览用的是**表单里正在填的值**，不是已存的值 —— 边改边看时薪怎么变。 */
  const previewUser: User | null = useMemo(() => {
    const cents = Math.round((Number(rate.take_home) || 0) * 100);
    if (cents <= 0) return null;
    return {
      ...current,
      take_home_cents: cents,
      pay_period: rate.pay_period,
      days_per_week_x10: rate.blocks.reduce((sum, block) => sum + block.days.length, 0) * 10,
      work_days: rate.blocks.flatMap((block) => block.days).sort((a, b) => a - b).join(","),
      week_work_minutes: rate.blocks.reduce(
        (sum, block) => sum + block.days.length * shiftMinutes(block.start, block.end), 0),
      commute_minutes: Number(rate.commute_minutes) || 0,
      extra_minutes: Number(rate.extra_minutes) || 0,
      time_as_workdays: rate.time_as_workdays ? 1 : 0,
    };
  }, [rate, current]);
  const ratePreview = previewUser ? centsPerHour(previewUser) : 0;
  const selfTestResults = useMemo(() => rateSelfTest(), []);
  const selfTestPassed = selfTestResults.filter(([, ok]) => ok).length;

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api("/api/settings/profile", { method: "PATCH", body: JSON.stringify({ ...profile, name: profile.name.trim(), avatar: profile.avatar.trim() }) });
      await onSaved("资料换上新装啦 💫");
    } catch (error) {
      await onSaved(error instanceof Error ? error.message : "没保存上，再试一次");
    } finally { setBusy(false); }
  };

  const saveGoals = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api("/api/settings", { method: "PATCH", body: JSON.stringify({ calorie_goal: Number(goals.calorie_goal), daily_budget: Number(goals.daily_budget), weekly_budget: Number(goals.weekly_budget), monthly_budget: Number(goals.monthly_budget) }) });
      await onSaved("目标和预算更新好了 ✨");
    } catch (error) {
      await onSaved(error instanceof Error ? error.message : "没保存上，再试一次");
    } finally { setBusy(false); }
  };

  const saveBody = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api("/api/settings/body", {
        method: "PATCH",
        body: JSON.stringify({
          height_cm: body.height_cm ? Number(body.height_cm) : 0,
          sex: body.sex,
          birth_year: body.birth_year ? Number(body.birth_year) : 0,
          activity_level: body.activity_level,
          target_weight: body.target_weight ? Number(body.target_weight) : 0,
          weekly_loss_grams: body.weekly_loss_grams,
        }),
      });
      await onSaved("身体资料存好啦 📏");
    } catch (error) {
      await onSaved(error instanceof Error ? error.message : "没保存上，再试一次");
    } finally { setBusy(false); }
  };

  const saveLeave = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api("/api/leaves", { method: "POST", body: JSON.stringify(leaveForm) });
      setLeaveForm({ ...leaveForm, note: "" });
      await onSaved("请假记下了，那天的工时会扣掉 🏖️");
    } catch (error) {
      await onSaved(error instanceof Error ? error.message : "没记上，再试一次");
    } finally { setBusy(false); }
  };

  const removeLeave = async (date: string) => {
    setBusy(true);
    try {
      await api(`/api/leaves/${date}`, { method: "DELETE" });
      await onSaved("销假了，那天算正常上班 ☀️");
    } catch (error) {
      await onSaved(error instanceof Error ? error.message : "没撤成，再试一次");
    } finally { setBusy(false); }
  };

  const saveRate = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api("/api/settings/rate", {
        method: "PATCH",
        body: JSON.stringify({
          take_home: Number(rate.take_home) || 0,
          pay_period: rate.pay_period,
          schedule: blocksToRows(rate.blocks),
          commute_minutes: Number(rate.commute_minutes) || 0,
          extra_minutes: Number(rate.extra_minutes) || 0,
          time_as_workdays: rate.time_as_workdays ? 1 : 0,
        }),
      });
      await onSaved(Number(rate.take_home) > 0
        ? "时薪存好啦，去记账页看看每笔花掉多少命 ⏳"
        : "已经关掉时间换算，界面回到只看钱");
    } catch (error) {
      await onSaved(error instanceof Error ? error.message : "没保存上，再试一次");
    } finally { setBusy(false); }
  };

  const savePin = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api("/api/settings/pin", { method: "PATCH", body: JSON.stringify(pins) });
      setPins({ current_pin: "", new_pin: "" });
      await onSaved("PIN 换好啦，记得别告诉对方 🤫");
    } catch (error) {
      await onSaved(error instanceof Error ? error.message : "PIN 没改成功");
    } finally { setBusy(false); }
  };

  return (
    <div className="stack page-stack settings-page">
      <section className="profile-banner" style={{ background: `${current.theme_color}22` }}>
        <span><Mascot kind={myKind} color={current.theme_color} size={48} /></span>
        <div>
          <p>当前登录</p>
          <h2>{current.name}</h2>
          <small>头像 {current.avatar} · 今天也要好好吃饭</small>
        </div>
        <i style={{ background: current.theme_color }} />
      </section>

      <form className="card settings-card" onSubmit={saveProfile}>
        <div className="section-title"><div><p className="eyebrow">个人资料</p><h2>换个名字和头像</h2></div><span>🪪</span></div>
        <div className="food-form-head">
          <label className="emoji-input"><span>头像</span><input value={profile.avatar} maxLength={8} onChange={(event) => setProfile({ ...profile, avatar: event.target.value })} aria-label="头像表情" /></label>
          <label><span>昵称</span><input value={profile.name} maxLength={20} onChange={(event) => setProfile({ ...profile, name: event.target.value })} placeholder="想被怎么称呼？" required /></label>
        </div>
        <fieldset className="swatch-field">
          <legend>专属颜色</legend>
          <div className="swatch-grid">
            {THEME_PRESETS.map((preset) => {
              const active = profile.theme_color.toLowerCase() === preset.value;
              return (
                <button
                  type="button"
                  key={preset.value}
                  className={`swatch ${active ? "active" : ""}`}
                  style={{ background: preset.value }}
                  aria-label={preset.name}
                  aria-pressed={active}
                  onClick={() => setProfile({ ...profile, theme_color: preset.value })}
                >
                  {active && <i>✓</i>}
                  <small>{preset.name}</small>
                </button>
              );
            })}
            <label className={`swatch custom ${isCustomColor ? "active" : ""}`} style={isCustomColor ? { background: profile.theme_color } : undefined}>
              {isCustomColor && <i>✓</i>}
              <input type="color" value={profile.theme_color} onChange={(event) => setProfile({ ...profile, theme_color: event.target.value.toLowerCase() })} aria-label="自定义颜色" />
              <small>自定义</small>
            </label>
          </div>
          <p className="hint-note">界面上属于你的那些小重点都会跟着变 🎨</p>
        </fieldset>
        <button className="primary-button" disabled={busy || !profile.name.trim() || !profile.avatar.trim()}>保存资料</button>
      </form>

      <form className="card settings-card" onSubmit={saveGoals}>
        <div className="section-title"><div><p className="eyebrow">我的目标</p><h2>吃得刚好，花得自在</h2></div><span>🎯</span></div>
        <label><span>每日卡路里目标 <small>千卡</small></span><input type="number" min="500" max="10000" value={goals.calorie_goal} onChange={(event) => setGoals({ ...goals, calorie_goal: event.target.value })} /></label>
        <div className="budget-input-grid">
          <label><span>每日预算 <small>RM</small></span><input type="number" min="0" step="0.01" value={goals.daily_budget} onChange={(event) => setGoals({ ...goals, daily_budget: event.target.value })} /></label>
          <label><span>每周预算 <small>RM</small></span><input type="number" min="0" step="0.01" value={goals.weekly_budget} onChange={(event) => setGoals({ ...goals, weekly_budget: event.target.value })} /></label>
        </div>
        <label><span>每月预算 <small>RM</small></span><input type="number" min="0" step="0.01" value={goals.monthly_budget} onChange={(event) => setGoals({ ...goals, monthly_budget: event.target.value })} /></label>
        <button className="primary-button" disabled={busy}>保存目标</button>
      </form>

      <form className="card settings-card rate-card" onSubmit={saveRate}>
        <div className="section-title"><div><p className="eyebrow">真实时薪</p><h2>把钱换算成人生</h2></div><span>⏳</span></div>
        <p className="hint-note rate-intro">
          填完之后，每一笔消费旁边都会多一行：这笔花掉你多少时间。
          算的不是税前时薪 —— 通勤和其他为工作花掉的时间也算你卖出去的命，所以通勤越长，东西越贵。
        </p>
        <div className="budget-input-grid">
          <label><span>税后收入 <small>RM</small></span><input type="number" min="0" step="0.01" inputMode="decimal" value={rate.take_home} onChange={(event) => setRate({ ...rate, take_home: event.target.value })} placeholder="3200" /></label>
          <label><span>发薪周期</span>
            <select value={rate.pay_period} onChange={(event) => setRate({ ...rate, pay_period: event.target.value as "月" | "年" })}>
              <option value="月">每月</option>
              <option value="年">每年</option>
            </select>
          </label>
        </div>
        <fieldset className="workday-field">
          <legend>我的班表 · 时间设一次，勾选适用哪几天</legend>
          <div className="block-list">
            {rate.blocks.map((block, index) => {
              const patch = (next: Partial<ShiftBlock>) => setRate({
                ...rate,
                blocks: rate.blocks.map((item, at) => (at === index ? { ...item, ...next } : item)),
              });
              /* 勾一天进这一块 = 把它从别的块里拿走：同一天只能属于一个时段，
                 否则那天到底几点上班就说不清了。 */
              const toggleDay = (day: number) => setRate({
                ...rate,
                blocks: rate.blocks.map((item, at) => {
                  if (at === index) {
                    return item.days.includes(day)
                      ? { ...item, days: item.days.filter((d) => d !== day) }
                      : { ...item, days: [...item.days, day].sort((a, b) => a - b) };
                  }
                  return { ...item, days: item.days.filter((d) => d !== day) };
                }),
              });
              return (
                <div className="block-card" key={index}>
                  <div className="block-time">
                    <input type="time" aria-label="上班时间" value={block.start}
                      onChange={(event) => patch({ start: event.target.value })} />
                    <span>–</span>
                    <input type="time" aria-label="下班时间" value={block.end}
                      onChange={(event) => patch({ end: event.target.value })} />
                    <em>{shiftHoursText(block.start, block.end)}</em>
                    {rate.blocks.length > 1 && (
                      <button type="button" className="block-del" aria-label="删掉这个时段"
                        onClick={() => setRate({ ...rate, blocks: rate.blocks.filter((_, at) => at !== index) })}>×</button>
                    )}
                  </div>
                  <div className="block-days">
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <button type="button" key={day}
                        className={`workday ${block.days.includes(day) ? "on" : ""}`}
                        aria-pressed={block.days.includes(day)}
                        onClick={() => toggleDay(day)}
                      >{"一二三四五六日"[day - 1]}</button>
                    ))}
                  </div>
                  {block.days.length === 0 && <p className="block-warn">这个时段还没勾任何一天</p>}
                </div>
              );
            })}
          </div>
          <button type="button" className="secondary-button block-add"
            onClick={() => setRate({ ...rate, blocks: [...rate.blocks, { start: "09:00", end: "13:00", days: [] }] })}>
            ＋ 另一个时段（例如周六半天）
          </button>
          <p className="hint-note">
            没被勾到的日子就是休息。一周共 <b>{(rate.blocks.reduce((sum, block) => sum + block.days.length * shiftMinutes(block.start, block.end), 0) / 60).toFixed(1)} 小时</b>，
            不用另外填工时，也不用打卡 —— 只有请假才按一下。
          </p>
        </fieldset>

        <div className="budget-input-grid">
          <label><span>通勤 <small>分钟 · 往返</small></span><input type="number" min="0" max="600" value={rate.commute_minutes} onChange={(event) => setRate({ ...rate, commute_minutes: event.target.value })} placeholder="90" /></label>
          <label><span>其他无偿 <small>分钟</small></span><input type="number" min="0" max="600" value={rate.extra_minutes} onChange={(event) => setRate({ ...rate, extra_minutes: event.target.value })} placeholder="0" /></label>
        </div>
        <label className="switch-row">
          <span>长时间按工作日显示 <small>14 小时 → 1 天 6 小时</small></span>
          <input type="checkbox" checked={rate.time_as_workdays} onChange={(event) => setRate({ ...rate, time_as_workdays: event.target.checked })} />
        </label>

        <div className="rate-preview">
          <b>{ratePreview > 0 ? rm(Math.round(ratePreview)) : "—"}</b>
          <div>
            <strong>每小时</strong>
            <small>{ratePreview > 0 ? `一个月卖掉 ${previewUser ? lifeHoursPerMonth(previewUser).toFixed(1) : "0"} 小时生命` : "填了税后收入才算得出来"}</small>
          </div>
        </div>
        {ratePreview > 0 && (
          <p className="hint-note rate-example">
            照这个算：一杯 RM6 的奶茶 = {previewUser ? lifeText(hoursOfCents(600, previewUser), previewUser) : ""}，
            一顿 RM60 的晚餐 = {previewUser ? lifeText(hoursOfCents(6000, previewUser), previewUser) : ""}。
          </p>
        )}
        <p className="rate-selftest">
          公式自检 {selfTestPassed}/{selfTestResults.length} 通过
          {selfTestPassed === selfTestResults.length ? " ✓" : " ✗ 有一条不过，上面的数先别信"}
        </p>
        <button className="primary-button" disabled={busy}>保存时薪</button>
      </form>

      <form className="card settings-card" onSubmit={saveLeave}>
        <div className="section-title"><div><p className="eyebrow">请假</p><h2>忘了记也能补回来</h2></div><span>🏖️</span></div>
        <p className="hint-note">
          平常不用管 —— 没有记录就是按班表正常上班。只有请假才记一条，
          那天的工时和工钱会跟着扣。<b>哪天忘了记，在这里补上就行。</b>
        </p>
        <div className="budget-input-grid">
          <label><span>哪一天</span><input type="date" value={leaveForm.local_date} max={today}
            onChange={(event) => setLeaveForm({ ...leaveForm, local_date: event.target.value })} required /></label>
          <label><span>请多久</span>
            <select value={leaveForm.kind}
              onChange={(event) => setLeaveForm({ ...leaveForm, kind: event.target.value as "全天" | "半天" })}>
              <option value="全天">全天</option>
              <option value="半天">半天</option>
            </select>
          </label>
        </div>
        <label><span>备注 <small>可不填</small></span><input value={leaveForm.note} maxLength={60}
          onChange={(event) => setLeaveForm({ ...leaveForm, note: event.target.value })} placeholder="例如：年假、病假" /></label>
        <button className="primary-button" disabled={busy}>记下这天请假</button>

        {myLeaves.length > 0 && (
          <div className="leave-list">
            {myLeaves.map((item) => (
              <div className="leave-row" key={item.id}>
                <span className="leave-dot">{item.kind === "半天" ? "🌤️" : "🏖️"}</span>
                <div className="leave-copy">
                  <strong>{item.local_date}</strong>
                  <small>{item.kind}{item.note ? ` · ${item.note}` : ""}</small>
                </div>
                <button type="button" className="undo-button danger" disabled={busy}
                  onClick={() => void removeLeave(item.local_date)}>销假</button>
              </div>
            ))}
          </div>
        )}
      </form>

      <form className="card settings-card" onSubmit={saveBody}>
        <div className="section-title"><div><p className="eyebrow">身体资料</p><h2>算 BMI 和每日建议用</h2></div><span>📏</span></div>
        <div className="budget-input-grid">
          <label><span>身高 <small>公分</small></span><input type="number" min="100" max="250" inputMode="numeric" value={body.height_cm} onChange={(event) => setBody({ ...body, height_cm: event.target.value })} placeholder="165" /></label>
          <label><span>出生年份</span><input type="number" min="1900" max="2100" inputMode="numeric" value={body.birth_year} onChange={(event) => setBody({ ...body, birth_year: event.target.value })} placeholder="1998" /></label>
        </div>
        <ChoicePills label="性别" options={[...SEX_OPTIONS]} value={body.sex} onChange={(sex) => setBody({ ...body, sex: sex as "女" | "男" })} icons={["🎀", "🧢"]} />
        <fieldset className="choice-field">
          <legend>平时活动量</legend>
          <div className="activity-grid">
            {ACTIVITY_OPTIONS.map((option) => (
              <button
                type="button"
                key={option}
                className={body.activity_level === option ? "active" : ""}
                onClick={() => setBody({ ...body, activity_level: option })}
              >
                <strong>{option}</strong>
                <small>{ACTIVITY_HINT[option]}</small>
              </button>
            ))}
          </div>
        </fieldset>
        <label><span>目标体重 <small>公斤 · 可不填</small></span><input type="number" min="20" max="400" step="0.1" inputMode="decimal" value={body.target_weight} onChange={(event) => setBody({ ...body, target_weight: event.target.value })} placeholder="想到达的数字" /></label>
        <ChoicePills
          label="每周想减多少"
          options={WEEKLY_LOSS_OPTIONS.map((option) => option.label)}
          value={WEEKLY_LOSS_OPTIONS.find((option) => option.grams === body.weekly_loss_grams)?.label ?? "0.5 公斤"}
          onChange={(label) => setBody({ ...body, weekly_loss_grams: WEEKLY_LOSS_OPTIONS.find((option) => option.label === label)?.grams ?? 500 })}
        />
        <p className="hint-note">每周 0.5 公斤是比较稳的速度。减太快掉的多半是水和肌肉，还容易反弹 🌱</p>
        <button className="primary-button" disabled={busy}>保存身体资料</button>
      </form>

      <section className="card settings-card">
        <div className="section-title"><div><p className="eyebrow">快捷选择</p><h2>管理常吃食物</h2></div><button className="circle-add" onClick={() => onFood()}>＋</button></div>
        {foods.length === 0 ? (
          <EmptyState kind={myKind} title="还没有常吃的" copy="加几样最爱的，以后一按就记好。" />
        ) : (
          <div className="settings-foods">
            {foods.map((food) => (
              <div key={food.id}>
                <span>{food.emoji}</span>
                <p><strong>{food.name}</strong><small>{food.calories} 千卡 · {food.default_meal}</small></p>
                <button onClick={() => onFood(food)}>编辑</button>
                <button className="danger-text" onClick={() => onDeleteFood(food.id)}>删除</button>
              </div>
            ))}
          </div>
        )}
      </section>

      <form className="card settings-card" onSubmit={savePin}>
        <div className="section-title"><div><p className="eyebrow">安全设置</p><h2>更换我的 PIN</h2></div><span>🔐</span></div>
        <div className="budget-input-grid">
          <label><span>当前 PIN</span><input type="password" inputMode="numeric" maxLength={4} value={pins.current_pin} onChange={(event) => setPins({ ...pins, current_pin: event.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="••••" /></label>
          <label><span>新的 PIN</span><input type="password" inputMode="numeric" maxLength={4} value={pins.new_pin} onChange={(event) => setPins({ ...pins, new_pin: event.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="••••" /></label>
        </div>
        <p className="hint-note">换好之后，其它设备上的登录会自动退出 🛡️</p>
        <button className="secondary-button" disabled={busy || pins.current_pin.length !== 4 || pins.new_pin.length !== 4}>更新 PIN</button>
      </form>

      <section className="card data-actions">
        <a href="/api/export" download>📦 备份我们的小账本 <span>→</span></a>
        <button onClick={() => void onLogout()}>👋 退出登录 <span>→</span></button>
      </section>
      <p className="version-note">二人食光 · 闲置 1 小时会自动帮你锁上 🔒</p>
    </div>
  );
}

function RecordRow({ icon, title, meta, value, life, color, badge, editable, onEdit, onDelete }: {
  icon: string; title: string; meta: string; value: string; life?: string | null; color: string; badge?: string; editable: boolean; onEdit: () => void; onDelete: () => void;
}) {
  return (
    <div className="record-row">
      <span className="record-icon" style={{ background: `${color}24` }}>{icon}</span>
      <div className="record-copy"><strong>{title}</strong><small>{meta}</small>{badge && <em>{badge}</em>}</div>
      {/* 金额下面挂一行时间成本 —— 账本的第二列。没填时薪就整行不出现。 */}
      {life ? <span className="record-amount"><b>{value}</b><i className="life-tag">{life}</i></span> : <b>{value}</b>}
      {editable && (
        <div className="row-actions">
          <button aria-label="编辑" onClick={onEdit}>✎</button>
          <button aria-label="删除" onClick={onDelete}>×</button>
        </div>
      )}
    </div>
  );
}

function EmptyState({ kind, title, copy }: { kind: MascotKind; title: string; copy: string }) {
  return (
    <div className="empty-state">
      <Mascot kind={kind} mood="sleepy" size={68} />
      <strong>{title}</strong>
      <p>{copy}</p>
    </div>
  );
}

function SettleSheet({ today, settlements, onClose, onSettle, onUndo }: {
  today: string; settlements: Settlement[]; onClose: () => void;
  onSettle: (date: string) => Promise<void>; onUndo: (date: string) => Promise<void>;
}) {
  const [date, setDate] = useState(today);
  const [busy, setBusy] = useState(false);
  const earliest = useMemo(() => addDays(today, -BACKFILL_DAYS), [today]);
  const existing = settlements.find((item) => item.local_date === date);

  return (
    <div className="sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="entry-sheet" role="dialog" aria-modal="true" aria-label="补结算">
        <div className="sheet-handle" />
        <div className="sheet-title">
          <div><p className="eyebrow">想起漏记的那天</p><h2>补结算 / 重新结算</h2></div>
          <button aria-label="关闭" onClick={onClose}>×</button>
        </div>
        <div className="settle-form">
          <label>
            <span>选一天</span>
            <input type="date" value={date} min={earliest} max={today} onChange={(event) => setDate(event.target.value || today)} />
          </label>
          <div className="settle-preview">
            {existing ? (
              <>
                <strong>{friendlyDate(date, today)} 已经结算过</strong>
                <small>花了 {rm(existing.spent_cents)} · {existing.saved_cents >= 0 ? `省下 ${rm(existing.saved_cents)}` : `超了 ${rm(-existing.saved_cents)}`}</small>
                <small>补记完消费后再结算一次，金额会自动重新算好。</small>
              </>
            ) : (
              <>
                <strong>{friendlyDate(date, today)} 还没结算</strong>
                <small>结算之后就能看到那天省下多少啦。</small>
              </>
            )}
          </div>
          <button
            className="primary-button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onSettle(date);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "正在算…" : existing ? "🔁 重新结算这一天" : "🎉 结算这一天"}
          </button>
          {existing && (
            <button
              className="danger-button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onUndo(date);
                } finally {
                  setBusy(false);
                }
              }}
            >
              🗑️ 撤销这天的结算
            </button>
          )}
          <p className="hint-note">只能补最近 {BACKFILL_DAYS} 天里的日子，也不能提前结算还没到的日子 🌙</p>
        </div>
      </section>
    </div>
  );
}

function EntrySheet({ sheet, today, me, onClose, onSaved }: {
  sheet: NonNullable<Sheet>; today: string; me: User; onClose: () => void; onSaved: (message: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const calorieItem = sheet.type === "calorie" ? sheet.item : undefined;
  const expenseItem = sheet.type === "expense" ? sheet.item : undefined;
  const foodItem = sheet.type === "food" ? sheet.item : undefined;
  const weightItem = sheet.type === "weight" ? sheet.item : undefined;
  const preset = sheet.type === "calorie" ? sheet.preset : undefined;
  const [calorie, setCalorie] = useState({
    food_name: calorieItem?.food_name ?? preset?.name ?? "",
    calories: String(calorieItem?.calories ?? preset?.calories ?? ""),
    meal_type: calorieItem?.meal_type ?? preset?.default_meal ?? "午餐",
    local_date: calorieItem?.local_date ?? today,
  });
  const [expense, setExpense] = useState({
    amount: expenseItem ? String(expenseItem.amount_cents / 100) : "",
    category: expenseItem?.category ?? "吃饭",
    scope: expenseItem?.scope ?? "个人",
    note: expenseItem?.note ?? "",
    sentiment: expenseItem?.sentiment ?? "",
    local_date: expenseItem?.local_date ?? today,
  });
  /* 边打字边算：这笔钱等于你多少命。共同支出只算你摊的那一半。 */
  const typedCents = Math.round((Number(expense.amount) || 0) * 100);
  const myShareCents = expense.scope === "共同" ? Math.floor(typedCents / 2) : typedCents;
  const typedLife = lifeOf(myShareCents, me);
  const [weight, setWeight] = useState({
    weight: weightItem ? (weightItem.weight_grams / 1000).toFixed(1) : "",
    local_date: weightItem?.local_date ?? today,
    note: weightItem?.note ?? "",
  });
  const [food, setFood] = useState({
    name: foodItem?.name ?? "",
    calories: String(foodItem?.calories ?? ""),
    default_meal: foodItem?.default_meal ?? "午餐",
    emoji: foodItem?.emoji ?? "🍽️",
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      if (sheet.type === "calorie") {
        await api(calorieItem ? `/api/calories/${calorieItem.id}` : "/api/calories", {
          method: calorieItem ? "PUT" : "POST",
          body: JSON.stringify({ ...calorie, calories: Number(calorie.calories), occurred_at: calorieItem?.occurred_at ?? new Date().toISOString() }),
        });
        await onSaved(calorieItem ? "这一口已经改好啦" : "记下了！这一口很值得 😋");
      } else if (sheet.type === "expense") {
        await api(expenseItem ? `/api/expenses/${expenseItem.id}` : "/api/expenses", {
          method: expenseItem ? "PUT" : "POST",
          body: JSON.stringify({ ...expense, amount: Number(expense.amount), spent_at: expenseItem?.spent_at ?? new Date().toISOString() }),
        });
        // 时间成本是按当下时薪现算的，不落库 —— 以后改时薪，历史每一笔的时间会一起变。
        await onSaved(expenseItem ? "这一笔已经改好啦" : "记下了！钱包很有安全感 🪙");
      } else if (sheet.type === "weight") {
        await api("/api/weights", {
          method: "POST",
          body: JSON.stringify({ ...weight, weight: Number(weight.weight) }),
        });
        await onSaved(weightItem ? "改好啦，数字更新了 🎈" : "记下啦，每天同一个时间称最准 🎈");
      } else {
        await api(foodItem ? `/api/foods/${foodItem.id}` : "/api/foods", {
          method: foodItem ? "PUT" : "POST",
          body: JSON.stringify({ ...food, calories: Number(food.calories) }),
        });
        await onSaved(foodItem ? "常吃食物更新好啦" : "已经加进快捷列表 🍽️");
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "没保存上，再试一次");
    } finally { setBusy(false); }
  };

  const title = sheet.type === "calorie"
    ? (calorieItem ? "编辑这一口" : "记下这一口")
    : sheet.type === "expense"
      ? (expenseItem ? "编辑这笔消费" : "记下这笔消费")
      : sheet.type === "weight"
        ? (weightItem ? "改一下这天的体重" : "记录今天的体重")
        : (foodItem ? "编辑常吃食物" : "添加常吃食物");

  return (
    <div className="sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="entry-sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-handle" />
        <div className="sheet-title">
          <div><p className="eyebrow">快速添加</p><h2>{title}</h2></div>
          <button aria-label="关闭" onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          {sheet.type === "calorie" && <>
            <label><span>食物名</span><input autoFocus value={calorie.food_name} onChange={(event) => setCalorie({ ...calorie, food_name: event.target.value })} placeholder="例如：海南鸡饭" required /></label>
            <div className="form-grid">
              <label><span>卡路里 <small>千卡</small></span><input type="number" min="1" max="10000" value={calorie.calories} onChange={(event) => setCalorie({ ...calorie, calories: event.target.value })} placeholder="650" required /></label>
              <label><span>日期</span><input type="date" value={calorie.local_date} max={today} onChange={(event) => setCalorie({ ...calorie, local_date: event.target.value })} required /></label>
            </div>
            <ChoicePills label="餐别" options={MEALS} value={calorie.meal_type} onChange={(meal_type) => setCalorie({ ...calorie, meal_type })} icons={MEALS.map((meal) => MEAL_ICON[meal])} />
          </>}
          {sheet.type === "expense" && <>
            <label className="amount-field"><span>金额（RM）</span><div><b>RM</b><input autoFocus type="number" min="0.01" step="0.01" value={expense.amount} onChange={(event) => setExpense({ ...expense, amount: event.target.value })} placeholder="0.00" required /></div></label>
            {/* 边打字边显示这笔等于多少命 —— 这一行就是整个时间视图的意义所在 */}
            {typedLife && (
              <p className="live-life">
                ⏳ 这笔花掉你 <b>{typedLife}</b> 的人生
                {expense.scope === "共同" && <small>（你摊一半）</small>}
              </p>
            )}
            <ChoicePills label="分类" options={CATEGORIES} value={expense.category} onChange={(category) => setExpense({ ...expense, category })} icons={CATEGORIES.map((category) => CATEGORY_ICON[category])} />
            <ChoicePills label="算谁的" options={["个人", "共同"]} value={expense.scope} onChange={(scope) => setExpense({ ...expense, scope: scope as "个人" | "共同" })} icons={["🧍", "💑"]} />
            <ChoicePills label="值不值 · 可不点" options={SENTIMENTS} value={expense.sentiment} onChange={(sentiment) => setExpense({ ...expense, sentiment: expense.sentiment === sentiment ? "" : sentiment as "" | "值" | "一般" | "后悔" })} icons={SENTIMENTS.map((item) => SENTIMENT_ICON[item])} />
            <label><span>备注 <small>可不填</small></span><input value={expense.note} onChange={(event) => setExpense({ ...expense, note: event.target.value })} placeholder="例如：周末约会晚餐" /></label>
            <label><span>日期</span><input type="date" value={expense.local_date} max={today} onChange={(event) => setExpense({ ...expense, local_date: event.target.value })} required /></label>
          </>}
          {sheet.type === "weight" && <>
            <label className="amount-field">
              <span>体重</span>
              <div>
                <input autoFocus type="number" min="20" max="400" step="0.1" inputMode="decimal" value={weight.weight} onChange={(event) => setWeight({ ...weight, weight: event.target.value })} placeholder="0.0" required />
                <b>公斤</b>
              </div>
            </label>
            <label><span>日期</span><input type="date" value={weight.local_date} max={today} onChange={(event) => setWeight({ ...weight, local_date: event.target.value })} required /></label>
            <label><span>备注 <small>可不填</small></span><input value={weight.note} maxLength={60} onChange={(event) => setWeight({ ...weight, note: event.target.value })} placeholder="例如：早上空腹" /></label>
            <p className="hint-note">同一天再记一次会覆盖掉，不会多出一条。建议固定早上起床后称，数字才好比较 🌅</p>
          </>}
          {sheet.type === "food" && <>
            <div className="food-form-head">
              <label className="emoji-input"><span>图标</span><input value={food.emoji} maxLength={8} onChange={(event) => setFood({ ...food, emoji: event.target.value })} /></label>
              <label><span>食物名</span><input autoFocus value={food.name} onChange={(event) => setFood({ ...food, name: event.target.value })} placeholder="例如：云吞面" required /></label>
            </div>
            <label><span>常用卡路里 <small>千卡</small></span><input type="number" min="1" max="10000" value={food.calories} onChange={(event) => setFood({ ...food, calories: event.target.value })} required /></label>
            <ChoicePills label="默认餐别" options={MEALS} value={food.default_meal} onChange={(default_meal) => setFood({ ...food, default_meal })} icons={MEALS.map((meal) => MEAL_ICON[meal])} />
          </>}
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button sheet-submit" disabled={busy}>{busy ? "正在保存…" : "保存并完成 ✓"}</button>
        </form>
      </section>
    </div>
  );
}

function ChoicePills({ label, options, value, onChange, icons }: {
  label: string; options: string[]; value: string; onChange: (value: string) => void; icons?: string[];
}) {
  return (
    <fieldset className="choice-field">
      <legend>{label}</legend>
      <div>
        {options.map((option, index) => (
          <button type="button" key={option} className={value === option ? "active" : ""} onClick={() => onChange(option)}>
            {icons?.[index] ? `${icons[index]} ` : ""}{option}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function CelebrationOverlay({ celebration, onClose, data, current, today }: {
  celebration: NonNullable<Celebration>; onClose: () => void; data: AppData; current: User; today: string;
}) {
  const monthSaved = data.settlements
    .filter((item) => item.user_id === current.id && item.local_date >= monthStart(today) && item.saved_cents > 0)
    .reduce((sum, item) => sum + item.saved_cents, 0);
  return (
    <div className={`celebration ${celebration.dual ? "dual" : ""}`} role="dialog" aria-modal="true">
      <div className="confetti" aria-hidden="true">
        {CONFETTI.map((piece, index) => <i key={index} style={piece} />)}
      </div>
      <div className="celebration-card">
        <img className="celebration-img" src="/mascots/celebrate.webp" alt="一起庆祝的小熊和小兔" />
        <p className="eyebrow">结算完成</p>
        <h2>{celebration.message}</h2>
        <p>{celebration.dual ? "两个会过日子的人，连钱包都在撒糖。" : `这天预算还剩 ${rm(celebration.saved)}，做得很漂亮！`}</p>
        <div className="monthly-saving"><span>本月累计省下</span><strong>{rm(monthSaved)}</strong></div>
        <button className="primary-button" onClick={onClose}>收下今天的小红花 🌸</button>
      </div>
    </div>
  );
}
